import * as React from 'react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Client,
  CustomMarker,
  CustomTextMarker,
  DIGESTION_FIELD_IDS,
  matchesExpectedText,
  FurtherConsiderations,
  MenstrualPhase,
  PanelField,
  PanelMarker,
  orderPractitionerSectionNames,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionOptionValue,
  practitionerSectionTitle,
  sectionRankingFor,
  SECTION_PANEL_MARKER_GETTERS,
} from '../../models';

import { FurtherConsiderationsEntryStore } from '../../stores/further-considerations-entry-store';
import { DisclaimerSection } from '../bloodworkz/disclaimer-section';
import { MyRangeDot } from './my-range-dot';
import { Card, Table, OverlayTrigger, Tooltip, Button, Image, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { MathUtils } from '../../utils/MathUtils';
import { digestionVisible } from '../../utils/digestionVisibility';
import { mvxVisible } from '../../utils/mvxVisibility';
import Moment from 'react-moment';
import { UserProfile } from '../../models/UserProfile';
import { resolveComparisonReportTitle } from '../../models/reportTitles';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import Storage from '@aws-amplify/storage';
import { PDFModal } from '../common/pdf-modal';
import { GenericModal } from '../common/generic-modal';
import { buildAIExportData } from '../../helpers/AIExportBuilder';
import { RawDataExportControl, downloadRawDataExport } from './raw-data-export';
import { AI_PROGRESS_SUMMARY_PROMPT } from '../../helpers/aiReviewPrompt';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { ClientNotes } from './client-notes';
import {
  renderNoteLine,
  MarkdownToolbar,
  NOTE_TEXT_COLOR,
  NOTE_WRAP,
  wrapSelection,
  formatMarkerForKeydown,
} from './note-markdown';
import { SectionNotesSummaryPanel } from './section-notes-summary-panel';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { toImageDataUrl } from '../../utils/imageDataUrl';
import { isSameBloodwork } from '../../utils/sameBloodwork';
import { aiProgressSummaryVisible } from '../../utils/aiSummaryVisibility';
import { Section } from '../../models';
import {
  comparisonSectionCost,
  comparisonSectionNeedsChunking,
  comparisonTextRowLines,
  packComparisonSectionRows,
  COMPARISON_PAGE_BUDGET,
  COMPARISON_ROW_COST,
  COMPARISON_SECTION_BLOCK_COST,
} from '../../utils/pdfComparisonSectionPagination';
import { faFileDownload as faFileDownloadSolid } from '@fortawesome/pro-solid-svg-icons';

/**
 * The report section behind each comparison-section key, so a custom marker
 * filed under a section on the analysis page lands in the same section's
 * comparison table. Keys are the ones in comparisonSectionOrder().
 *
 * 'customMarkers' is absent on purpose — it is the appendix that holds whatever
 * was NOT placed, so it can never be a placement target.
 */
const COMPARISON_SECTION_BY_KEY: { [key: string]: Section } = {
  rbc: Section.RBC,
  iron: Section.IRON,
  immune: Section.WBC,
  platelets: Section.PLATELETS,
  glucose: Section.GLUCOSE,
  kidney: Section.KIDNEY,
  proteins: Section.PROTEINS,
  digestion: Section.DIGESTION,
  nutrients: Section.NUTRIENTS,
  electrolytes: Section.ELECTROLYTES,
  lipids: Section.LIPIDS,
  inflammation: Section.INFLAMMATION_OXIDATIVE_STRESS,
  liver: Section.LIVER_BILIARY,
  thyroid: Section.THYROID,
  stressAdrenal: Section.STRESS_ADRENAL,
  hormones: Section.SEX_HORMONE,
  prostate: Section.PROSTATE,
};

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');
const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');
const logo = require('../../assets/imgs/logo.png');

const NUM_RESULTS_TO_COMPARE: number = 10;
const NUM_RESULTS_TO_COMPARE_FOR_PDF: number = 5;

// Stands in for "no custom logo, print the bundled LabSmarts mark" as a
// headerLogoKey. Not a URL, so it can never collide with a practitioner's.
const BUNDLED_LOGO_KEY = 'bundled-labsmarts-logo';

// Breathing room above a panel that starts a PDF page (the Insights table and
// Overall Progress & Trends chunks each open with a forced page break, so this
// sits at the very top of the page). Matches the Disclaimer wrapper's
// padding-top so all three panels clear the page edge by the same amount.
// Counted in the chunkers' HEADER_HEIGHT — raising it here without raising
// that would let a chunk run past the bottom of the page.
const TOP_OF_PAGE_SPACER = '24px';

interface Props {
  bloodworks: Bloodwork[];
  bloodworkRanges: BloodworkRange[];
  currentUserProfile: UserProfile;
  onExportToPdf: Function;
  onHidePdfModal: Function;
  bloodworkEntryStore: BloodworkEntryStore;
  clientEntryStore: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  showPdfModal: boolean;
  showUpgradeModal: boolean;
  numResultsToCompare: number;
  headerLogoSrc?: string; // resolved preview URL for the modal's <Image id="logo">
  // The logo headerLogoSrc was resolved FROM (a custom logo URL, or the
  // bundled-default sentinel). Kept so a later render can tell a resolved logo
  // from a stale one — see syncHeaderPreviewLogo.
  headerLogoKey?: string;
  showHiddenRows: boolean; // toggle to show/hide rows with no value
  // Toggles to drop the Alarm and Lab Min/Max columns (screen + PDF).
  // UNDEFINED until the practitioner touches the checkbox, which means "follow
  // the Report Settings default" — read through alarmColumnsHidden() and
  // labColumnsHidden(), never straight off the state. Undefined rather than a
  // value copied in at construction because the profile loads asynchronously
  // and may not be there yet when this card mounts.
  hideAlarmColumns?: boolean;
  hideLabColumns?: boolean;
  withNotes: boolean; // toggle to include practitioner notes in PDF (changed name to Insights)
  showSectionNotes: boolean; // toggle the Section Notes column in the notes table (screen + PDF)
  furtherConsiderations: FurtherConsiderations;
  generatingSummary: boolean; // AI "Overall Progress & Trends" request in flight
  /** Which half of Overall Progress & Trends is open. The printing one leads. */
  overallProgressTab: string;
  aiSummaryDraft: string; // latest AI-generated draft to inject into the notes field
  // The Notes column of the comparison tables (comparisonSectionNotes on the
  // client). One section's note is editable at a time; its draft lives here
  // until Save writes it to the client or Cancel drops it.
  editingNotesSectionKey?: string;
  // What the editor OPENS with, not what is in it. The live text belongs to
  // ComparisonNoteEditor so typing never re-renders the tables; the parent
  // reads it back through noteEditor on Save.
  notesSeed: string;
  // The three note blocks this report's PDF carries, as chosen for THIS
  // client: the Notes column beside each table, the Insights & Section Notes
  // table, and the Overall Progress & Trends card. Undefined means untouched,
  // which reads as the practitioner's Report Setting — same shape as
  // hideAlarmColumns. Session-only and reset when the client changes, so the
  // choice is per client and never writes back to the profile.
  includeNotesColumnInPdf?: boolean;
  includeInsightsNotesInPdf?: boolean;
  includeOverallProgressInPdf?: boolean;
  savingNotes: boolean;
};

// What the comparison report shows for one collection date. The Insights note
// (Section.NOTES) is only half of it: a practitioner can type everything in the
// per-section Notes tabs and never touch Insights, and those notes have to come
// across too — otherwise that test looks like it has no notes at all.
/**
 * The history newest test first, on a COPY.
 *
 * Which end of the history a marker's position comes from is a decision, not a
 * detail. Custom markers are gathered across every test in the comparison, and
 * gathering them oldest-first pinned each one's row to the order it was first
 * typed in, years ago — later tests could correct its label but never move it.
 * That left the practitioner's own ordering (the drag handles in the entry
 * table, which set the order on the analysis screen and in the PDF) unable to
 * reach the comparison report at all.
 *
 * Newest first, and first-seen wins, so the newest test that carries a marker
 * decides where it sits. A marker dropped from recent tests keeps a place among
 * the ones it was last filed with, rather than being pushed to the end.
 *
 * A COPY because Array.reverse mutates, and this history is shared by every
 * section builder on the page.
 */
function newestFirst(history: BloodworkAnalysis[]): BloodworkAnalysis[] {
  return [...history].reverse();
}

function insightsTextFor(bloodwork: Bloodwork): string {
  return (bloodwork?.bloodworkNotes?.findNote(Section.NOTES)?.text || '').trim();
}

// That test's consolidated Section Notes — the practitioner's edited override
// when there is one, otherwise each section's own note under its "## Label".
function sectionNotesTextFor(bloodworkAnalysis: BloodworkAnalysis): string {
  return SectionNotesSummaryPanel.bodyText(bloodworkAnalysis).trim();
}

// Insights first, then the section notes beneath it. Either half on its own is
// enough to give the date a row.
export function comparisonNotesTextFor(
  bloodworkAnalysis: BloodworkAnalysis,
): string {
  return [
    insightsTextFor(bloodworkAnalysis?.bloodwork),
    sectionNotesTextFor(bloodworkAnalysis),
  ]
    .filter(Boolean)
    .join('\n\n');
}

/**
 * How many table rows a section note takes up, wrapping included.
 *
 * The one place that arithmetic lives. Two things need it and they MUST agree:
 * the section cost that decides page breaks (comparisonSectionCost) and the
 * hard stop on the note editor (MAX_NOTE_ROWS). If the editor counted lines its
 * own way it could accept a note that pagination then measures as overflowing,
 * which is the bug the stop exists to prevent.
 *
 * A typed line is not a rendered row: one long bullet fills three of them, so
 * counting newlines would wave a note three times over the limit through.
 */
export function noteRowsUsed(note: string): number {
  if (!note) return 0;
  return note
    .split('\n')
    .reduce(
      (rows, line) => rows + Math.max(1, Math.ceil(line.length / NOTE_WRAP_CHARS)),
      0,
    );
}

/**
 * The most rows a section note may take: what one landscape PDF page holds.
 *
 * The Notes cell is rowSpanned across its whole section, so it sets the table's
 * height whenever it outruns the markers beside it. A note held to a page's
 * worth can stretch its table but can never push it onto a second page, which
 * is what strands a row or two on their own.
 *
 * Enforced where the text is TYPED, not where it is drawn. Cutting it at
 * display time hid writing the practitioner had already done and still believed
 * was in the report, which is worse than not letting them write it: a note is
 * clinical content going to a client and must not be quietly half delivered.
 * Everything typed is shown in full, on screen and in the PDF alike.
 *
 * 14 is the bottom of the "~14–16 rows a landscape page holds" that the
 * tall-section threshold below is set against. Worth re-measuring against a
 * real export before it is raised: the 34-character wrap this counts with is
 * itself conservative, so the stop bites sooner than the page truly demands.
 */
export const MAX_NOTE_ROWS = 14;

/**
 * Characters on one line of the Notes column AS PRINTED.
 *
 * Measured off a real export rather than guessed: in the PDF a 62-character
 * heading sits on one line and a 73-character bullet wraps, so the printed
 * column holds somewhere just over 70.
 *
 * The column is WIDER than this on screen, about 1.7x, because the browser
 * table spans a monitor while the PDF's inner box is ~1085px. Nothing can make
 * one number true of both, so this one is the printed width: the limit exists
 * to protect the printed page, and the counter beside the editor says "printed
 * lines" for that reason. The practical effect is that a line can look like one
 * line in the box and cost two in print, which is the safe direction.
 *
 * Raising this does NOT loosen page breaks. The cap is expressed in rows
 * (MAX_NOTE_ROWS) and comparisonSectionCost counts the note in those same rows,
 * so the tallest a note can ever make a table is unchanged; a wider line only
 * means more characters fit inside those rows.
 */
const NOTE_WRAP_CHARS = 70;

// The budget line once the note is full. Amber rather than red: the note is at
// its limit, which is a boundary and not an error.
const NOTE_LIMIT_COLOR = '#8a6100';

// One collection date's notes as the PDF renders them: each column already
// sliced to what fits on this page. isCont marks the continuation halves.
export type NoteChunkRow = {
  collectionDate: string;
  insightsSplit: string;
  sectionSplit: string;
  isCont: boolean;
};

/**
 * Pre-paginate the notes table for the PDF: an array of page chunks, each an
 * array of rows. html2pdf can't split a row (and the date cell can't be
 * rowSpanned across a break), so both columns are sliced against the SAME page
 * budget — a row that spills carries both halves onto the next page in step,
 * otherwise one column would run ahead and pair the wrong text with the wrong
 * date. Row height is the taller column, not the sum of the two.
 */
export function paginateNoteRows(
  entries: {
    collectionDate: string;
    insights: string;
    sectionNotes: string;
  }[],
  opts: { showSectionNotes: boolean },
): NoteChunkRow[][] {
  // PAGE GEOMETRY, measured off a real export rather than guessed. html2pdf
  // renders into a container whose CSS width is pageSize.inner.width in mm
  // (worker.js:110) and its jsPDF format defaults to a4 — so landscape a4
  // (297x210mm) less the [3,5,10,5] margins set in PDFModal gives a 287x197mm
  // inner box: ~1085 CSS px wide, ~744 CSS px tall per page.
  // All the safety margin lives HERE, in the page budget — never in WRAP_CHARS
  // below. A too-small divisor is wrong once per line and compounds: at 74
  // instead of 82, a 163-character bullet scores 3 lines instead of 2, and 8
  // such bullets lose a third of a page. 44px of reserve absorbs a couple of
  // under-estimated lines instead.
  const MAX_PAGE_HEIGHT = 700;
  // Top spacer (24px, see TOP_OF_PAGE_SPACER) + card header + table header.
  const HEADER_HEIGHT = 76;
  const ROW_PADDING = 12; // Internal padding per row
  const LINE_HEIGHT = 18; // Height per line; measured ~17.7, so it leans safe
  // Characters on one rendered line, per column — the REAL figure, no cushion:
  // the column's share of the ~1083px table, less 24px of cell padding, over
  // the ~5.6px average character width. 45% -> 82, 40% -> 73, 80% -> 150.
  const INS_WRAP = opts.showSectionNotes ? 82 : 150;
  const SEC_WRAP = 73;

  // Take as many lines as fit in `budget` and hand back the rest for the next
  // page. forceOne stops a single over-tall line from stalling a fresh page.
  const takeLines = (
    lines: string[],
    budget: number,
    forceOne: boolean,
    wrapChars: number,
  ) => {
    const taken: string[] = [];
    let height = 0;
    let i = 0;
    while (i < lines.length) {
      const h =
        Math.max(1, Math.ceil(lines[i].length / wrapChars)) * LINE_HEIGHT;
      if (height + h > budget && (taken.length > 0 || !forceOne)) break;
      taken.push(lines[i]);
      height += h;
      i++;
    }
    return { taken, rest: lines.slice(i), height };
  };

  const noteChunks: NoteChunkRow[][] = [];
  let currentChunk: NoteChunkRow[] = [];
  let currentHeight = HEADER_HEIGHT;

  entries.forEach(entry => {
    let insLines = entry.insights ? entry.insights.split('\n') : [];
    let secLines =
      opts.showSectionNotes && entry.sectionNotes
        ? entry.sectionNotes.split('\n')
        : [];
    if (insLines.length === 0 && secLines.length === 0) return;

    // If the current chunk is too full to fit even two more lines, flush it
    // before starting a new note so the next collection date starts on a fresh page.
    if (
      currentChunk.length > 0 &&
      currentHeight + LINE_HEIGHT * 2 > MAX_PAGE_HEIGHT
    ) {
      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
    }

    let partsAdded = 0;
    while (true) {
      const budget = MAX_PAGE_HEIGHT - currentHeight;
      const fresh = currentChunk.length === 0;
      const ins = takeLines(insLines, budget, fresh, INS_WRAP);
      const sec = takeLines(secLines, budget, fresh, SEC_WRAP);

      if (ins.taken.length === 0 && sec.taken.length === 0) {
        // Nothing fits in what's left of this page — start a fresh one. (Can't
        // loop forever: on a fresh chunk `forceOne` always takes a line.)
        noteChunks.push(currentChunk);
        currentChunk = [];
        currentHeight = HEADER_HEIGHT;
        continue;
      }

      currentChunk.push({
        collectionDate: entry.collectionDate,
        insightsSplit: ins.taken.join('\n'),
        sectionSplit: sec.taken.join('\n'),
        isCont: partsAdded > 0,
      });
      currentHeight += Math.max(ins.height, sec.height) + ROW_PADDING;
      insLines = ins.rest;
      secLines = sec.rest;
      if (insLines.length === 0 && secLines.length === 0) break;

      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
      partsAdded++;
    }
  });

  if (currentChunk.length > 0) {
    noteChunks.push(currentChunk);
  }

  return noteChunks;
}

class PractitionerNoteRow extends React.Component<
  {
    bloodwork: Bloodwork;
    bloodworkEntryStore: BloodworkEntryStore;
    /**
     * The analysis this row's notes belong to. Needed to edit Section Notes:
     * that cell is not a stored field but the consolidated body, and saving it
     * writes the same Section.SECTION_NOTES override the panel on that test's
     * own report writes.
     */
    bloodworkAnalysis?: BloodworkAnalysis;
    sectionNotesText?: string;
    showSectionNotesColumn?: boolean;
  },
  { isEditing: boolean; text: string; sectionNotesDraft: string }
> {
  constructor(props: any) {
    super(props);
    const note = props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.state = {
      isEditing: false,
      text: note ? note.text : '',
      sectionNotesDraft: props.sectionNotesText || '',
    };
  }

  textareaRef = React.createRef<HTMLTextAreaElement>();
  sectionNotesRef = React.createRef<HTMLTextAreaElement>();
  // This note's own row — where Save scrolls back to.
  rowRef = React.createRef<HTMLTableRowElement>();

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.state.isEditing && !prevState.isEditing) {
      [this.textareaRef.current, this.sectionNotesRef.current].forEach(el => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    }
  }
  componentWillReceiveProps(nextProps: any) {
    // A row pointed at a DIFFERENT test reseeds even mid-edit: what was typed
    // belongs to the test that was there, and Save would write it over this
    // one's note. Rows are keyed by test id, so this is the safety net rather
    // than the normal path.
    const switchedTest = !isSameBloodwork(
      nextProps.bloodwork,
      this.props.bloodwork,
    );
    if (!this.state.isEditing || switchedTest) {
      const note = nextProps.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
      this.setState({
        isEditing: switchedTest ? false : this.state.isEditing,
        text: note ? note.text : '',
        sectionNotesDraft: nextProps.sectionNotesText || '',
      });
    }
  }
  handleEdit = () =>
    this.setState({
      isEditing: true,
      sectionNotesDraft: this.props.sectionNotesText || '',
    });

  // Same Bold/Italic/Underline tools as the Insights panel this note is typed
  // in — it's the same note (Section.NOTES), just shown per collection date.
  applyFormat = (value: string, selStart: number, selEnd: number) => {
    this.setState({ text: value }, () => {
      const el = this.textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(selStart, selEnd);
      });
    });
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const marker = formatMarkerForKeydown(e);
    if (marker && this.textareaRef.current) {
      e.preventDefault();
      const { value, selStart, selEnd } = wrapSelection(
        this.textareaRef.current,
        marker,
      );
      this.applyFormat(value, selStart, selEnd);
    }
  };

  handleSave = (e: any) => {
    e.preventDefault();
    const { bloodwork, bloodworkEntryStore, bloodworkAnalysis } = this.props;
    let note = bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    if (!note) {
      note = bloodwork.bloodworkNotes?.addNote(Section.NOTES, '');
    }
    if (note) note.text = this.state.text;
    // Section Notes go through the panel's own writer, so an edit made here
    // and one made on that test's report produce exactly the same record — one
    // Section.SECTION_NOTES override, and none at all when nothing changed.
    if (bloodworkAnalysis && this.canEditSectionNotes()) {
      SectionNotesSummaryPanel.writeOverrideOn(
        bloodworkAnalysis,
        this.state.sectionNotesDraft,
      );
    }
    // Scroll back to this row once the save resolves — the spinner modal locks
    // body scrolling while it's up, so without this the page lands at the top
    // of the report.
    const scrollBack = () => scrollPanelIntoView(this.rowRef.current);
    bloodworkEntryStore.saveBloodwork(bloodwork)
      .then(scrollBack)
      .catch((err) => {
        console.error('Failed to save bloodwork note', err);
        alert('Failed to save your note. Please try again.');
        scrollBack();
      });
    this.setState({ isEditing: false });
  };
  handleCancel = () => {
    const note = this.props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.setState({
      isEditing: false,
      text: note ? note.text : '',
      sectionNotesDraft: this.props.sectionNotesText || '',
    });
  };

  /** Section Notes are editable only where the column is shown at all. */
  private canEditSectionNotes = (): boolean =>
    !!this.props.showSectionNotesColumn && !!this.props.bloodworkAnalysis;

  /**
   * Drop the edit back to each section's own notes, the same Reset the panel on
   * that test's report offers. Only meaningful once an override exists.
   */
  handleResetSectionNotes = () => {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis) {
      return;
    }
    if (
      !window.confirm(
        'Clear your edits and rebuild Section Notes from each section’s notes?',
      )
    ) {
      return;
    }
    this.setState({
      sectionNotesDraft: SectionNotesSummaryPanel.autoText(bloodworkAnalysis),
    });
  };

  render() {
    const {
      bloodwork,
      bloodworkAnalysis,
      sectionNotesText,
      showSectionNotesColumn,
    } = this.props;
    const { isEditing, text, sectionNotesDraft } = this.state;
    const canEditSectionNotes = this.canEditSectionNotes();
    const hasSectionNotesOverride =
      !!bloodworkAnalysis &&
      SectionNotesSummaryPanel.hasOverride(bloodworkAnalysis);
    return (
      <tr ref={this.rowRef}>
        <td>
          <Moment format="MM/DD/YYYY">{bloodwork.collectionDate}</Moment>
        </td>
        <td>
          {isEditing ? (
            <>
              <MarkdownToolbar
                getTextarea={() => this.textareaRef.current}
                onApply={this.applyFormat}
              />
              <textarea
                ref={this.textareaRef}
                value={text}
                onChange={e => {
                  this.setState({ text: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={this.handleKeyDown}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  overflow: 'hidden',
                  resize: 'none',
                  // A raw textarea keeps the browser's own font, so it read
                  // bigger (and in a different face) than the formatted note it
                  // turns into. Match the page.
                  fontFamily: 'inherit',
                  fontSize: '12px',
                }}
              />
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
                Formatting:&nbsp;<code>## Section</code>&nbsp;for section
                headings,&nbsp;<code>**bold**</code>,&nbsp;<code>*italic*</code>
                ,&nbsp;<code>_underline_</code>. These render formatted when you
                Save.
              </div>
            </>
          ) : (
            // Formatted, not raw markup — a note typed with **bold** in the
            // Insights panel has to read the same here. Line by line, because
            // the PDF build of this table paginates it that way.
            <div
              style={{
                whiteSpace: 'pre-wrap',
                ...NOTE_WRAP,
                minHeight: '1.5em',
                color: NOTE_TEXT_COLOR,
              }}
            >
              {(text || '').trim() ? (
                (text || '').split('\n').map((line, lIdx) => (
                  <div key={lIdx}>{renderNoteLine(line)}</div>
                ))
              ) : (
                // A test can have section notes and no Insights note — say so
                // rather than leaving a cell that reads as broken.
                <span style={{ color: '#adb5bd' }}>&mdash;</span>
              )}
            </div>
          )}
        </td>
        {/* Section Notes ride in their own column so they never read as part of
            the Insights note, which summarises the whole test. Editing them here
            writes the same consolidated override the panel on that test's own
            report writes, so the two screens can never disagree about what the
            Section Notes say. */}
        {showSectionNotesColumn && (
          <td>
            {isEditing && canEditSectionNotes ? (
              <>
                <textarea
                  ref={this.sectionNotesRef}
                  value={sectionNotesDraft}
                  onChange={e => {
                    this.setState({ sectionNotesDraft: e.target.value });
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    overflow: 'hidden',
                    resize: 'none',
                    fontFamily: 'inherit',
                    fontSize: '12px',
                  }}
                />
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
                  Built from each section&rsquo;s own notes. Editing it here
                  keeps what you type, on this test only, until you reset it.
                  {hasSectionNotesOverride || sectionNotesDraft ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        style={{ fontSize: '12px' }}
                        onClick={this.handleResetSectionNotes}
                      >
                        Reset to the section notes
                      </button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (sectionNotesText || '').trim() ? (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  ...NOTE_WRAP,
                  color: NOTE_TEXT_COLOR,
                }}
              >
                {sectionNotesText!.split('\n').map((line, lIdx) => (
                  <div key={`sn-${lIdx}`}>{renderNoteLine(line)}</div>
                ))}
              </div>
            ) : (
              <span style={{ color: '#adb5bd' }}>&mdash;</span>
            )}
          </td>
        )}
        <td style={{ textAlign: 'center' }}>
          {isEditing ? (
            <>
              <button
                className="btn btn-sm btn-primary"
                onClick={this.handleSave}
                style={{ marginRight: '4px' }}
              >
                Save
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-sm btn-primary"
              onClick={this.handleEdit}
            >
              Edit
            </button>
          )}
        </td>
      </tr>
    );
  }
}

// Hormone Balance no longer carries a row count of its own. It had one for
// years — 24 compact rows beneath the heading and column header — and the row
// packer every other section pages with arrived at the same 24 from a printed
// page (COMPARISON_ROWS_PER_PDF_PAGE), so the count is not lost, just no longer
// kept in two places. The hormone table's rows are the COMPACT ones (3px
// padding, see the style block in renderHormoneSectionPdf), so a budget solved
// on ordinary rows is the conservative side to be on.

/** The screen table for a section: where Save scrolls back to. */
function comparisonTableDomId(sectionKey: string): string {
  return `comparison-table-${sectionKey}`;
}

/** What comparisonTable needs to know beyond its rows. */
type ComparisonTableOptions = {
  /**
   * Which section this table is. The Notes column keys its Edit on it, so a
   * table built without one (the direct render tests) has no Notes column.
   */
  sectionKey?: string;
  /** The PDF's copy: no Edit/Save, and Notes only on a with-notes export. */
  isPdf?: boolean;
  /**
   * Leave the Notes column off this table. The Hormone Balance PDF splits one
   * section across page-sized tables, and the section's one note belongs on
   * the first of them, not repeated on every "(cont.)" page.
   */
  /**
   * A continuation page of a section that was split across pages: keep the
   * Notes column, print nothing in it.
   *
   * It used to drop the column outright, and that changed the TABLE's geometry
   * rather than just its content. With a Notes column the other columns shrink
   * to their content and Notes takes the rest (.comparison-with-notes in
   * App.css); without it they spread across the full width. So a continuation
   * page came out with a wider result column, its words no longer wrapping, and
   * a missing column against a first page that had one — two different tables
   * for one section.
   */
  blankNotes?: boolean;
};

/**
 * A two-word column label on two lines, so the column is only as wide as
 * its longer word, with the shorter word centered under the longer. The
 * block hugs the left of the cell like every other header, so "Min" sits
 * centered under "Optimal" rather than drifting to the column's middle. The
 * space before the break keeps the label reading as two words wherever the
 * text is taken as text.
 */
function stackedLabel(first: string, second: string): React.ReactNode {
  return (
    <span style={{ display: 'inline-block', textAlign: 'center' }}>
      {first} <br />
      {second}
    </span>
  );
}

/**
 * A collection date header on two lines, MM/DD over YYYY, so a date column is
 * only as wide as "09/24" instead of the whole date. The second line is free:
 * this header row is already two lines tall wherever it renders, because the
 * Optimal Min / Optimal Max labels beside it always go through stackedLabel.
 *
 * The year keeps all four digits. Stacking is what buys the width, and once the
 * date is on two lines the MM/DD line is the wider one, so "26" would be no
 * narrower than "2026" — the century is free, and this report is a clinical
 * document that gets printed and read years later.
 *
 * The break replaces the second slash rather than following it: a date reads as
 * a date from its shape, and a line ending in a slash looked like a truncation.
 */
function stackedDate(collectionDate: string): React.ReactNode {
  return (
    <span style={{ display: 'inline-block', textAlign: 'center' }}>
      <Moment format="MM/DD">{collectionDate}</Moment>
      <br />
      <Moment format="YYYY">{collectionDate}</Moment>
    </span>
  );
}

// Size a section-note textarea to its cell: as tall as the rows it spans,
// and taller still when the text needs it, so the box reads as the cell
// rather than as a small field floating in the middle of it. The spanned
// height is read once, on mount, before the textarea itself can have
// stretched the cell; after that only the text can make it taller.
function fitNoteTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  if (el.dataset.fillHeight === undefined) {
    const cell = el.parentElement;
    let fill = 0;
    if (cell) {
      const style = window.getComputedStyle(cell);
      fill =
        cell.clientHeight -
        (parseFloat(style.paddingTop) || 0) -
        (parseFloat(style.paddingBottom) || 0);
      // Anything else in the cell (the gauge summary above the box) keeps
      // its own height; the box takes what is left.
      Array.from(cell.children).forEach(child => {
        if (child === el) return;
        const childStyle = window.getComputedStyle(child);
        fill -=
          (child as HTMLElement).offsetHeight +
          (parseFloat(childStyle.marginTop) || 0) +
          (parseFloat(childStyle.marginBottom) || 0);
      });
    }
    el.dataset.fillHeight = String(Math.max(0, fill));
  }
  el.style.height = 'auto';
  const fill = Number(el.dataset.fillHeight) || 0;
  el.style.height = `${Math.max(fill, el.scrollHeight)}px`;
}

/**
 * The Notes cell's editor: the textarea and the length budget under it.
 *
 * It owns the draft rather than the report around it. The draft used to live on
 * BloodworkComparisonAnalysis state, so every keystroke re-rendered every
 * comparison table on the page — and, before the analyses were memoized,
 * re-analyzed the client's whole test history along with them. Typing lagged
 * badly as a result.
 *
 * Save reads the text through a ref because the Save button is in the table's
 * HEADER, not beside the box, so the two cannot share a closure. That is what
 * `draft()` is for.
 */
class ComparisonNoteEditor extends React.Component<
  { seed: string },
  { draft: string }
> {
  state = { draft: this.props.seed || '' };

  /** The live text, for the parent's Save. */
  draft(): string {
    return this.state.draft;
  }

  private onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;
    const next = el.value;
    // The hard stop. Anything that does not make the note LONGER is
    // always allowed, even when it is already over: a note saved before
    // this limit existed, or one pasted in whole, has to stay editable
    // or it could never be shortened back under the line.
    //
    // Measured against the draft in STATE, not the one this render
    // closed over, so a burst of keystrokes between renders is judged
    // against what the note actually is rather than what it was when
    // the textarea was last drawn.
    const kept = this.state.draft || '';
    const current = noteRowsUsed(kept);
    if (noteRowsUsed(next) > MAX_NOTE_ROWS && noteRowsUsed(next) > current) {
      // Put the text back AND put the caret back. This is a controlled
      // textarea, so refusing the change alone leaves the DOM holding
      // the rejected text until React restores it from state, and that
      // restore drops the caret at the very END of the note. Typing
      // then carried on at the bottom of the box instead of where the
      // practitioner was, which read as the text jumping to the last
      // line. Restore here, in the same tick, before anything repaints.
      // Back by however much was refused, so a rejected paste returns
      // the caret to where the paste began rather than past it.
      const refused = Math.max(0, next.length - kept.length);
      const caret = Math.max(
        0,
        Math.min((el.selectionStart ?? kept.length) - refused, kept.length),
      );
      el.value = kept;
      el.setSelectionRange(caret, caret);
      return;
    }
    this.setState({ draft: next });
    fitNoteTextarea(el);
  };

  render() {
    const draft = this.state.draft || '';
    const rowsUsed = noteRowsUsed(draft);
    const atLimit = rowsUsed >= MAX_NOTE_ROWS;
    return (
      <>
        <textarea
          ref={fitNoteTextarea}
          value={draft}
          rows={3}
          onChange={this.onChange}
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '72px',
            overflow: 'hidden',
            resize: 'none',
            fontFamily: 'inherit',
            fontSize: '12px',
          }}
        />
        {/* Same markup the other notes take (note-markdown.tsx), minus the
            "## Section" headings, which have no place in a one-section note. */}
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
          {/* The length budget, in front of the practitioner while they type
              rather than as a surprise when the keyboard stops responding. A
              note longer than a page would push its section onto a second one
              and strand a marker row or two there. */}
          <div
            className="comparison-notes-budget"
            style={{ color: atLimit ? NOTE_LIMIT_COLOR : undefined }}
          >
            {rowsUsed} of {MAX_NOTE_ROWS} printed lines
            {atLimit
              ? ' — full. This note is as long as one printed page holds.'
              : '. The box is wider than the printed column, so a long line here can take two in print.'}
          </div>
          Formatting:&nbsp;<code>**bold**</code>,&nbsp;<code>*italic*</code>
          ,&nbsp;<code>_underline_</code>. These render formatted when you
          Save.
        </div>
      </>
    );
  }
}

export class BloodworkComparisonAnalysis extends React.Component<Props, State> {
  // Anchors the "Test Results Comparison Tables" card so we can restore scroll to its
  // top after a toggle re-orders/re-renders the sections (otherwise the page
  // jumps mid-table to a section like "Blood Oxygen & Red Blood Cell Function").
  private comparisonCardRef = React.createRef<HTMLDivElement>();

  // The open section-note editor, which holds its own draft (see
  // ComparisonNoteEditor). Save is in the table header, away from the box, so
  // this is how it reaches the text.
  private noteEditor = React.createRef<ComparisonNoteEditor>();

  // The logo resolution currently in flight, if any (see
  // ensureHeaderPreviewLogo). On the instance rather than in state because it
  // guards work, not rendering: a setState per render pass is what it exists to
  // avoid.
  private resolvingLogoKey?: string;

  /**
   * Scroll the comparison card title back to the top of the viewport, offset by
   * the sticky header so the title isn't hidden beneath it. Mirrors the
   * sticky-offset pattern in scrollToComparison (bloodwork-analysis-container).
   */
  private scrollComparisonTitleIntoView = () => {
    // Defer to the next frame so the measurement happens after the table has
    // re-rendered and the browser has settled layout — otherwise a toggle that
    // triggers extra renders (e.g. Hide blank rows dropping whole sections)
    // scrolls to a stale position and lands inconsistently.
    requestAnimationFrame(() => {
      const element = this.comparisonCardRef.current;
      if (!element) return;
      const sticky = document.querySelector(
        '.App-sticky-top',
      ) as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top =
        element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
    });
  };

  state: State = {
    showPdfModal: false,
    showUpgradeModal: false,
    numResultsToCompare: NUM_RESULTS_TO_COMPARE,
    headerLogoSrc: undefined,
    headerLogoKey: undefined,
    showHiddenRows: false, // default: hide empty rows
    hideAlarmColumns: undefined, // default: the Report Settings setting
    hideLabColumns: undefined, // default: the Report Settings setting
    withNotes: false,
    showSectionNotes: true, // default: show the Section Notes column
    furtherConsiderations: new FurtherConsiderations(),
    generatingSummary: false,
    overallProgressTab: 'progress-client',
    aiSummaryDraft: '',
    editingNotesSectionKey: undefined,
    notesSeed: '',
    savingNotes: false,
  };

  componentDidUpdate(prevProps: Props) {
    // The profile usually arrives AFTER this component has mounted, so the
    // cover logo has to be picked up when it does rather than once at mount.
    this.syncHeaderPreviewLogo();

    // A draft belongs to the client it was typed for. If the comparison is
    // pointed at a different client mid-edit, drop it rather than let Save
    // write it onto the wrong record.
    const prevClientId = prevProps.bloodworks?.[0]?.clientId;
    const clientId = this.props.bloodworks?.[0]?.clientId;
    if (this.state.editingNotesSectionKey && prevClientId !== clientId) {
      this.cancelEditingNotes();
    }
    // A new client starts from the Report Settings defaults. The checkboxes
    // are a choice about THIS client's report, so carrying them to the next
    // one would silently drop a block from a report nobody chose it for.
    if (prevClientId !== clientId) {
      this.setState({
        includeNotesColumnInPdf: undefined,
        includeInsightsNotesInPdf: undefined,
        includeOverallProgressInPdf: undefined,
      });
    }
  }

  /**
   * The client the comparison is for: the store's live record when it has one
   * (the object Overall Progress & Trends saves through, and the Notes column
   * saves the same way), else the copy embedded on the test.
   */
  private comparisonClient(): Client | undefined {
    const bloodwork = this.props.bloodworks && this.props.bloodworks[0];
    if (!bloodwork) return undefined;
    return (
      this.props.clientEntryStore?.clients?.find(
        c => c.id === bloodwork.clientId,
      ) || bloodwork.client
    );
  }

  /** Whether the Notes column appears on screen at all. Default true. */
  private notesColumnOnScreenEnabled(): boolean {
    return this.props.currentUserProfile?.showComparisonNotesOnScreen ?? true;
  }

  /**
   * The three "Include ... in PDF" checkboxes under the card header.
   *
   * Under it rather than in it: the header already carries Hide Alarm, Hide
   * Lab, Hide Blank Rows and both export links, and three more checkboxes in
   * that row is more than it reads well with. These sit on the white body
   * directly below, aligned right so they land under the export links they
   * qualify.
   *
   * Each starts from its Report Setting and applies to this client's report
   * only — nothing here writes back to the profile, and switching client puts
   * all three back to the settings. Shown whatever the export, because they
   * describe what "Export to PDF w/ notes" will carry; a without-notes export
   * prints none of the three either way.
   */
  private renderPdfNoteIncludes(): React.ReactNode {
    const box = (
      id: string,
      label: string,
      checked: boolean,
      onChange: (value: boolean) => void,
    ) => (
      <label
        htmlFor={id}
        className="d-flex align-items-center"
        style={{
          gap: '6px',
          marginBottom: 0,
          fontWeight: 'normal',
          fontSize: '13px',
          color: '#495057',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        {label}
      </label>
    );

    return (
      <div
        className="d-flex align-items-center justify-content-end flex-wrap"
        style={{ gap: '16px', marginBottom: '8px' }}
      >
        <span style={{ fontSize: '13px', color: '#6c757d' }}>
          Include in Export to PDF w/ Notes:
        </span>
        {box(
          'include-notes-column-in-pdf',
          'Notes column',
          this.notesColumnInPdfEnabled(),
          value => this.setState({ includeNotesColumnInPdf: value }),
        )}
        {box(
          'include-insights-notes-in-pdf',
          'Insights & Section Notes',
          this.insightsNotesInPdfEnabled(),
          value => this.setState({ includeInsightsNotesInPdf: value }),
        )}
        {box(
          'include-overall-progress-in-pdf',
          'Overall Progress & Trends',
          this.overallProgressInPdfEnabled(),
          value => this.setState({ includeOverallProgressInPdf: value }),
        )}
      </div>
    );
  }

  /**
   * Whether the Notes column prints on the with-notes PDF: this report's
   * checkbox once it has been touched, otherwise the Report Setting.
   */
  private notesColumnInPdfEnabled(): boolean {
    return (
      this.state.includeNotesColumnInPdf ??
      this.props.currentUserProfile?.showComparisonNotesInPdf ??
      true
    );
  }

  /**
   * Whether the export prints the "Insights & Section Notes by Collection
   * Date" table, and whether it prints the "Overall Progress & Trends" card.
   *
   * Two conditions each, and they mean different things: the export BUTTON
   * chooses whether this is a report with notes at all, and the setting
   * chooses which of the note blocks that report carries. All three blocks —
   * these two and the Notes column beside each table — used to ride on the
   * button alone, so wanting the per-section Notes column meant taking the
   * summaries after it too. Both settings default true, which is what the
   * with-notes export has always printed.
   */
  private insightsNotesInPdfEnabled(): boolean {
    return (
      this.state.includeInsightsNotesInPdf ??
      this.props.currentUserProfile?.showComparisonInsightsNotesInPdf ??
      true
    );
  }

  private overallProgressInPdfEnabled(): boolean {
    return (
      this.state.includeOverallProgressInPdf ??
      this.props.currentUserProfile?.showComparisonOverallProgressInPdf ??
      true
    );
  }

  private printsInsightsNotesBlock(): boolean {
    return !!this.state.withNotes && this.insightsNotesInPdfEnabled();
  }

  private printsOverallProgressBlock(): boolean {
    return !!this.state.withNotes && this.overallProgressInPdfEnabled();
  }

  /**
   * Whether the Alarm Min/Max columns are hidden: the header checkbox once it
   * has been touched, otherwise the practitioner's Report Settings default.
   * False for a profile saved before that setting existed.
   */
  private alarmColumnsHidden(): boolean {
    return (
      this.state.hideAlarmColumns ??
      this.props.currentUserProfile?.hideAlarmColumnsInComparison ??
      false
    );
  }

  /** The same for the Lab Min/Max columns. */
  private labColumnsHidden(): boolean {
    return (
      this.state.hideLabColumns ??
      this.props.currentUserProfile?.hideLabColumnsInComparison ??
      false
    );
  }

  /**
   * The analyses the tables read, rebuilt only when the data behind them
   * changes.
   *
   * Building ONE is the whole analysis pipeline — init(),
   * populatePanelMarkersArray() and performAnalysis(), which is sixteen
   * sub-analyses and the root-cause pass — and this ran for every test in the
   * comparison on every render. Typing one character in a section note
   * re-analyzed the client's whole history from scratch, which is what made the
   * keyboard lag; so did each Hide Alarm / Hide Lab / Hide Blank Rows click.
   *
   * The inputs are compared by identity, which is enough because the store
   * hands back the same arrays and the same Bloodwork objects until the data
   * really changes: a saved test is swapped into the list as a NEW object
   * (refreshSavedBloodwork), so the cache falls out on its own.
   */
  private analysisHistoryCache?: {
    bloodworks: Bloodwork[];
    bloodworkRanges: BloodworkRange[];
    practitionerSections?: { name?: string }[];
    history: BloodworkAnalysis[];
  };

  private analysisHistory(
    bloodworks: Bloodwork[],
    bloodworkRanges: BloodworkRange[],
    practitionerSections?: { name?: string }[],
  ): BloodworkAnalysis[] {
    const cached = this.analysisHistoryCache;
    if (
      cached &&
      cached.bloodworkRanges === bloodworkRanges &&
      cached.practitionerSections === practitionerSections &&
      cached.bloodworks.length === bloodworks.length &&
      cached.bloodworks.every((bw, i) => bw === bloodworks[i])
    ) {
      return cached.history;
    }

    const history: BloodworkAnalysis[] = bloodworks.map(bloodwork => {
      const bloodworkAnalysis = new BloodworkAnalysis({
        bloodwork,
        bloodworkRanges,
        bloodworkFields: PanelField.ALL_FIELDS,
        // For the Section Notes digest, which asks each analysis for its own
        // practitioner sections (see SectionNotesSummaryPanel.bodyText). The
        // tables above get their order from practitionerSectionNames.
        //
        // Each test ranks by ITS OWN arrangement first: the digest prints one
        // block per test, so a test whose cards were arranged prints its
        // sections in the order it was arranged in, whatever its neighbours
        // were arranged as. The cache above still keys on the profile list,
        // which is sound because an arrangement lives on the Bloodwork and
        // those are compared by identity.
        practitionerSections: sectionRankingFor(
          bloodwork?.customMarkerSectionOrder,
          practitionerSections,
        ),
      });
      bloodworkAnalysis.init();
      bloodworkAnalysis.populatePanelMarkersArray();
      // init() only builds the markers; every derived finding (the anemia
      // gauge the RBC note quotes, the root causes under it) comes from
      // performAnalysis(), the same call the analysis screen, the analysis
      // PDF and the AI export make. It touches none of the marker values
      // the tables and trend thumbs read.
      bloodworkAnalysis.performAnalysis();
      return bloodworkAnalysis;
    });

    if (history.length >= 2) {
      history[history.length - 1].preparePreviousPanelMarkers(
        history[history.length - 2],
      );
    }

    this.analysisHistoryCache = {
      bloodworks: [...bloodworks],
      bloodworkRanges,
      practitionerSections,
      history,
    };
    return history;
  }

  /** The saved comparison notes by section key. Never undefined. */
  private savedSectionNotes(): { [sectionKey: string]: string } {
    return this.comparisonClient()?.comparisonSectionNotes || {};
  }

  /** This section's saved note, trimmed; empty when there is none. */
  private sectionNoteFor(sectionKey: string): string {
    return (this.savedSectionNotes()[sectionKey] || '').trim();
  }

  /**
   * @param seed what the editor opens with: the saved note, or, when there is
   *   none yet, the gauge summary LabSmarts writes for the section, so the
   *   practitioner can change it before it is saved as theirs.
   */
  private startEditingNotes = (sectionKey: string, seed: string) => {
    this.setState({ editingNotesSectionKey: sectionKey, notesSeed: seed });
  };

  private cancelEditingNotes = () =>
    this.setState({ editingNotesSectionKey: undefined, notesSeed: '' });

  /**
   * Write the draft onto the client and save it the way Overall Progress &
   * Trends does (ClientNotes.saveOrUpdate). The store's spinner locks the page
   * while the save runs and leaves it at the top, so scroll back to the table
   * once it resolves.
   */
  private saveSectionNote = (sectionKey: string) => {
    const client = this.comparisonClient();
    const { clientEntryStore } = this.props;
    if (!client || !clientEntryStore) return;
    const notes = { ...(client.comparisonSectionNotes || {}) };
    // The editor holds the text, so without it there is nothing to save. Bail
    // rather than fall back to the seed: the seed is emptied on save and on
    // cancel, and an empty note DELETES the section's entry, so a fallback
    // turns "the box isn't there" into "wipe what was written". Save is only
    // rendered while the editor is, which makes this unreachable rather than
    // routine.
    const editor = this.noteEditor.current;
    if (!editor) return;
    const text = editor.draft().trim();
    // A cleared note is dropped, not stored as an empty string.
    if (text) {
      notes[sectionKey] = text;
    } else {
      delete notes[sectionKey];
    }
    client.comparisonSectionNotes = notes;
    clientEntryStore.setCurrentClient(client);
    this.setState({ savingNotes: true });
    const scrollBack = () =>
      scrollPanelIntoView(comparisonTableDomId(sectionKey));
    Promise.resolve(clientEntryStore.saveOrUpdate())
      .then(() => {
        this.setState({
          editingNotesSectionKey: undefined,
          notesSeed: '',
          savingNotes: false,
        });
        scrollBack();
      })
      .catch(err => {
        console.error('Failed to save the comparison note', err);
        alert('Failed to save the note. Please try again.');
        this.setState({ savingNotes: false });
        scrollBack();
      });
  };

  /**
   * A section's note: the practitioner's own text and nothing else.
   *
   * LabSmarts used to write the section's gauge reading and root causes in
   * here, shown until a note was saved. That was removed: with two collection
   * dates the summary ran to 38 lines against a 14-line page, and because a
   * saved note is never rewritten, one saved FROM a summary kept printing the
   * old dates and readings after a new test arrived, with nothing saying it had
   * gone stale. The column is a running note the practitioner keeps, and the
   * Insights panels remain where the gauge reading is read.
   */
  private noteBlocksFor(
    sectionKey: string,
    history: BloodworkAnalysis[],
  ): { saved: string; shown: string } {
    const saved = this.sectionNoteFor(sectionKey);
    return { saved, shown: saved };
  }


  /**
   * The one Notes cell of a table, spanning every row so it reads beside the
   * whole section rather than beside any one marker: the note formatted, or
   * the editor for it. While editing, a section with a gauge summary offers
   * to put the current summary back over what is typed (the same Reset the
   * Section Notes column has).
   */
  private sectionNoteCell(
    shown: string,
    isEditing: boolean,
    rowSpan: number,
  ): React.ReactNode {
    if (!isEditing) {
      return (
        <td className="comparison-notes-cell" rowSpan={rowSpan}>
          {shown ? (
            <div
              className="comparison-notes-text"
              style={{
                whiteSpace: 'pre-wrap',
                ...NOTE_WRAP,
                color: NOTE_TEXT_COLOR,
              }}
            >
              {shown.split('\n').map((line, lIdx) => (
                <div key={lIdx}>{renderNoteLine(line)}</div>
              ))}
            </div>
          ) : null}
        </td>
      );
    }
    return (
      <td className="comparison-notes-cell" rowSpan={rowSpan}>
        <ComparisonNoteEditor
          ref={this.noteEditor}
          seed={this.state.notesSeed || ''}
        />
      </td>
    );
  }

  /**
   * Build the raw-data payload, send it to the AI (Bedrock) via the client
   * endpoint, and drop the returned summary into the "Overall Progress & Trends"
   * field as an editable draft (the practitioner reviews, then Saves).
   */
  onGenerateSummary = async () => {
    const { bloodworks, bloodworkRanges, clientEntryStore } = this.props;
    const { furtherConsiderations } = this.state;
    if (this.state.generatingSummary) return;
    this.setState({ generatingSummary: true });
    try {
      // No overallPractitionerNotes and no supplements: this draft lands in a
      // client-facing report field, so it is built from the marker data alone.
      const payload = buildAIExportData(
        bloodworks,
        bloodworkRanges,
        furtherConsiderations,
      );
      // This lands in a client-facing report field, so use the plain-language,
      // non-diagnostic prompt (not the practitioner one embedded in the export).
      const summary = await clientEntryStore.generateAiSummary({
        instructions: AI_PROGRESS_SUMMARY_PROMPT,
        analysisHistory: payload.analysisHistory,
      });
      this.setState({ aiSummaryDraft: summary || '' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AI summary generation failed', err);
      const status =
        (err && err.response && err.response.status) ||
        (err && err.message) ||
        'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Could not generate the summary (${status}). Please try again.`);
    } finally {
      this.setState({ generatingSummary: false });
    }
  };

  // ----------- Helper to Hide Rows with No Values ---------
  /** Returns true if this marker has a value in at least one result in the history.
   *  Special-case menstrualPhase to consider altValue as a "value".
   */
  private fieldHasAnyValue(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    return history.some(bwa => {
      const pm = this.findPanelMarker(field, bwa);
      if (!pm) return false;

      if (field.id === 'menstrualPhase') {
        const alt = pm.altValue;

        // Treat undefined, null, or empty/whitespace string as "no value"
        // So menstrual phase behaves like other markers when hide blank rows is ON.
        return alt !== undefined && alt !== null && String(alt).trim() !== '';
      }

      return typeof pm.hasValue === 'function' && pm.hasValue();
    });
  }

  /** Returns true if any field in the section has a value in the entire history */
  private sectionHasAnyValues(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): boolean {
    return fields.some(f => this.fieldHasAnyValue(f, history));
  }

  // ----------- Section ordering ---------
  /**
   * Section keys in the order they should render — the same order as the
   * test-results analysis page, for both the screen table and the PDF.
   */
  private comparisonSectionOrder(): string[] {
    return [
      'rbc',
      'iron',
      'glucose',
      'lipids',
      'thyroid',
      // Between Thyroid and Nutrient Status, matching the analysis
      // page and the PDF packer.
      'digestion',
      'nutrients',
      'proteins',
      'immune',
      'inflammation',
      'platelets',
      'liver',
      'kidney',
      'electrolytes',
      'stressAdrenal',
      'hormones',
      'prostate',
      // Last shipped section, matching where it sits on the analysis page.
      'mvx',
      // Practitioner-typed, per-test markers always come last — they are an
      // appendix to the shipped panels.
      'customMarkers',
    ];
  }

  /**
   * Builds the Hormone Balance section (title + table), honoring "Hide blank rows"
   * and the menstrual-phase inclusion rules. Returns null when it should be hidden.
   */
  private buildHormoneSection(
    history: BloodworkAnalysis[],
    gender: any,
    isPdf: boolean = false,
  ): { title: string; table: React.ReactNode } | null {
    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = this.withChartedSectionFields(
      'hormones',
      [
        ...PanelField.HORMONE_FIELDS_1,
        ...PanelField.HORMONE_FIELDS_2,
      ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id)),
      history,
    );

    const hideBlanks = !this.state.showHiddenRows;

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    // Custom markers filed under Hormones are placed against the hormone
    // fields alone — menstrual phase is prepended afterwards, so a recorded
    // phase with no hormone results can't be what makes the section look
    // populated to the placement guard.
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...this.withPlacedCustomFields('hormones', hormoneFieldsBase, history),
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    const table = this.comparisonTable(
      history,
      hormoneFieldsForTable,
      gender,
      this.textMarkersForComparisonKey('hormones', history),
      { sectionKey: 'hormones', isPdf },
    );
    return table ? { title: 'Hormone Balance', table } : null;
  }

  /**
   * Renders the Hormone Balance section for the PDF, splitting it across
   * multiple pages when its rows exceed one page (packComparisonSectionRows,
   * the same packer every other section uses). Each spillover page repeats the column header
   * and carries a "Hormone Balance (cont.)" heading + a page break, mirroring
   * the "Insights by Collection Date (cont.)" / MOO "(cont.)" pattern. Without
   * the chunking, the full ~29-marker table is taller than a landscape page and
   * html2pdf slices it mid-row (the reported "cut off onto the next page" bug).
   * Returns null when the section should be hidden.
   *
   * @param history     bloodwork history (one column per collection date)
   * @param gender      client gender; menstrual phase is hidden for males
   * @param breakBefore force a page break before the first chunk
   */
  private renderHormoneSectionPdf(
    history: BloodworkAnalysis[],
    gender: any,
    breakBefore: boolean,
  ): React.ReactNode {
    const hideBlanks = !this.state.showHiddenRows;

    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = this.withChartedSectionFields(
      'hormones',
      [
        ...PanelField.HORMONE_FIELDS_1,
        ...PanelField.HORMONE_FIELDS_2,
      ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id)),
      history,
    );

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    // Custom markers filed under Hormones are placed against the hormone
    // fields alone — menstrual phase is prepended afterwards, so a recorded
    // phase with no hormone results can't be what makes the section look
    // populated to the placement guard.
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...this.withPlacedCustomFields('hormones', hormoneFieldsBase, history),
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    // Mirror comparisonTable's row-visibility filter so the chunk boundaries
    // line up exactly with the rows that will actually render.
    const visibleFields = hormoneFieldsForTable.filter(field => {
      if (field.isHiddenFromComparison) return false;
      if (field.id === 'menstrualPhase' && gender === 'M') return false;
      const hasAny = this.fieldHasAnyValue(field, history);
      return hideBlanks ? hasAny : true;
    });

    // Text markers filed under Hormones. They follow the measurements, in the
    // order the table prints them, and they are PAGED WITH THEM: they used to be
    // handed whole to the last chunk whatever was already on it, so a hormone
    // panel that filled its page and then took twenty urinalysis-style rows ran
    // off the bottom of it.
    const hormoneTextMarkers = this.textMarkersForComparisonKey(
      'hormones',
      history,
    );

    if (visibleFields.length === 0 && hormoneTextMarkers.length === 0) {
      return null;
    }

    /*
     * One mechanism, shared with every other section.
     *
     * This split its fields at a fixed count of its own and left the text rows
     * out of the arithmetic. The row packer next door counts both, in the units
     * a printed page was measured in, and always returns at least one chunk — so
     * a section that is nothing but text results is no longer a dropped section.
     */
    const chunks = packComparisonSectionRows(
      visibleFields.length,
      this.textRowLinesFor(hormoneTextMarkers, history),
    );

    return (
      <>
        {chunks.map((chunk, chunkIdx) => {
          // The section's one note prints beside the first table only.
          const table = this.comparisonTable(
            history,
            visibleFields.slice(chunk.fieldStart, chunk.fieldEnd),
            gender,
            hormoneTextMarkers.slice(chunk.textStart, chunk.textEnd),
            { sectionKey: 'hormones', isPdf: true, blankNotes: chunkIdx > 0 },
          );
          if (!table) return null;
          const isCont = chunkIdx > 0;
          return (
            <React.Fragment key={`hormone-chunk-${chunkIdx}`}>
              {(breakBefore || isCont) && (
                <div className="html2pdf__page-break" />
              )}
              {/* The heading goes INSIDE the keep-together block, with its
                  table, the way every other section's does (.pdf-section wraps
                  both — see renderPdfSectionsAnalysisOrder). It used to sit
                  outside, and a heading outside the block that gets moved is a
                  heading left behind: "Hormone Balance" printed at the foot of
                  one page and its table at the top of the next. This was the
                  only section built that way, and the only one it happened to. */}
              <div className="pdf-section">
                <div style={{ height: '8px' }} />
                <h5>
                  <strong>Hormone Balance{isCont ? ' (cont.)' : ''}</strong>
                </h5>
                <div
                  className="hormone-pdf-section"
                  style={{
                    marginBottom: 0,
                    paddingBottom: 10,
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                  }}
                >
                  <style>
                    {`
                      .hormone-pdf-section td,
                      .hormone-pdf-section th {
                        padding-top: 3px !important;
                        padding-bottom: 3px !important;
                      }
                    `}
                  </style>
                  {table}
                </div>
                <div style={{ height: '8px' }} />
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /**
   * How many lines each of a section's text results will take in its column.
   *
   * The page budget needs this per ROW, not per section: a urinalysis page of
   * sixteen "Not observed" and "Absent Risk" rows is a full page, and sixteen
   * rows of "Negative" leave a quarter of it empty. Measured against the
   * LONGEST value the row prints across the collections, because the columns
   * wrap in step and the tallest cell sets the row.
   */
  private textRowLinesFor(
    textMarkers: any[],
    history: BloodworkAnalysis[],
  ): number[] {
    return textMarkers.map(textMarker => {
      const longest = history.reduce((widest, bwa) => {
        const value = (this.textValueFor(textMarker, bwa) || '').trim();
        return value.length > widest.length ? value : widest;
      }, '');
      return comparisonTextRowLines(longest);
    });
  }

  /**
   * Render one comparison section as page-sized chunks for the PDF.
   *
   * The general form of what renderHormoneSectionPdf does for Hormone Balance:
   * a table longer than a page cannot be kept whole and must not simply flow,
   * because html2pdf carries the overflow onto the next page with no heading
   * and no column header above it. A reader meeting the second half of Liver
   * Function at the top of a page has nothing telling them what they are
   * looking at. Each chunk is a page of rows, rendered as its own table, under
   * a heading that repeats as "Urinalysis (cont.)".
   *
   * Rows are taken in the order the table prints them — charted markers first,
   * then text results — so a split lands where the page runs out rather than
   * always between the two kinds.
   *
   * The section's note prints beside the FIRST chunk only. It belongs to the
   * section rather than to a page of it, and repeating it under every
   * continuation is how a two-line note becomes six. The continuations keep the
   * Notes COLUMN and leave it empty (`blankNotes`): dropping the column changes
   * how every other column is sized, so a page without it is a different table.
   *
   * @param history     bloodwork history (one column per collection date)
   * @param section     the section being split: its key and its title
   * @param fields      the charted markers, already resolved for this section
   * @param textMarkers the text results, already resolved for this section
   * @param breakBefore force a page break before the first chunk
   */
  private renderChunkedSectionPdf(
    history: BloodworkAnalysis[],
    section: { key: string; title: string },
    fields: PanelField[],
    textMarkers: any[],
    breakBefore: boolean,
  ): React.ReactNode {
    /*
     * Sliced against the rows that will RENDER, not the fields handed in.
     *
     * comparisonTable drops a field with no value in the history while "Hide
     * blank rows" is on, so slicing the raw list would wrap a page's worth of
     * indices around rows that are not there: pages of two rows, and chunk
     * boundaries nowhere near where the page actually fills.
     */
    const visibleFields = fields.filter(
      field => this.countVisibleFields([field], history) > 0,
    );
    const chunks = packComparisonSectionRows(
      visibleFields.length,
      this.textRowLinesFor(textMarkers, history),
    );

    return (
      <>
        {chunks.map((chunk, chunkIdx) => {
          const isCont = chunkIdx > 0;
          const table = this.comparisonTable(
            history,
            visibleFields.slice(chunk.fieldStart, chunk.fieldEnd),
            undefined,
            textMarkers.slice(chunk.textStart, chunk.textEnd),
            {
              sectionKey: section.key,
              isPdf: true,
              blankNotes: isCont,
            },
          );
          if (!table) return null;
          return (
            <React.Fragment key={`${section.key}-chunk-${chunkIdx}`}>
              {(breakBefore || isCont) && (
                <div className="html2pdf__page-break" />
              )}
              <div className="pdf-section">
                <div style={{ height: '8px' }} />
                <h5>
                  <strong>
                    {section.title}
                    {isCont ? ' (cont.)' : ''}
                  </strong>
                </h5>
                <div className="section-block">{table}</div>
                <div style={{ height: '8px' }} />
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /**
   * Resolves the comparison sections (title + table) in the currently selected
   * order, dropping any section that has no table / is hidden by "Hide blank rows".
   * Shared by the on-screen UI (both orders) and the PDF (analysis order only).
   */
  private buildComparisonSections(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
    isPdf: boolean = false,
  ): {
    key: string;
    title: string;
    table: React.ReactNode;
    /** The rows the table was built from, for the PDF's chunked renderer. */
    fields?: PanelField[];
    textMarkers?: any[];
  }[] {
    const hideBlanks = !this.state.showHiddenRows;

    /*
     * A section, plus the two row lists its table was built from.
     *
     * The PDF needs those lists, not just the finished table: a section longer
     * than a page is re-rendered a page of rows at a time under a repeated
     * "(cont.)" heading, and there is no way to slice a built React table. They
     * ride along with the section rather than being resolved a second time in
     * the renderer, because resolving them twice is how the printed table and
     * the thing that paginates it drift apart.
     */
    const make = (
      title: string,
      built: {
        node: React.ReactNode;
        fields: PanelField[];
        textMarkers: any[];
      } | null,
    ) =>
      built
        ? {
            title,
            table: built.node,
            fields: built.fields,
            textMarkers: built.textMarkers,
          }
        : null;

    /** The same shape for the two sections that do not go through table(). */
    const built = (
      node: React.ReactNode | null,
      fields: PanelField[],
      textMarkers: any[],
    ) => (node ? { node, fields, textMarkers } : null);

    // Every section's table, built the same way for the screen and the PDF:
    // the two render identical tables, so one helper builds both.
    //
    // Takes the section KEY rather than a finished field list, because a
    // section now needs two lists resolved for it — the charted markers filed
    // into it and the text ones — and passing the key once is the only way the
    // two cannot be resolved for different sections by mistake.
    //
    // The field list comes from the KEY as well, via
    // shippedFieldsForComparisonKey — the same call the PDF height estimate and
    // the custom-marker placement guard make. Passing a list in per section is
    // what let the rendered table and those two drift: a section could list one
    // set of fields on screen and be paginated against another.
    const table = (key: string, sectionGender?: any) => {
      const fields = this.withPlacedCustomFields(
        key,
        this.shippedFieldsForComparisonKey(key, history),
        history,
      );
      const textMarkers = this.textMarkersForComparisonKey(key, history);
      return built(
        this.comparisonTable(history, fields, sectionGender, textMarkers, {
          sectionKey: key,
          isPdf,
        }),
        fields,
        textMarkers,
      );
    };

    const sections: {
      [key: string]: {
        title: string;
        table: React.ReactNode;
        // Absent for Hormone Balance, which paginates itself — see
        // renderHormoneSectionPdf.
        fields?: PanelField[];
        textMarkers?: any[];
      } | null;
    } = {
      rbc: make(
        'Blood Oxygen & Red Blood Cell Function',
        table('rbc'),
      ),
      iron:
        hideBlanks && !this.anyIronCoreMarkerHasValue(history)
          ? null
          : make(
              'Iron Status & Utilization',
              table('iron'),
            ),
      immune:
        !whiteBloodCellsHasValues && hideBlanks
          ? null
          : make(
              'Immune Activity',
              table('immune'),
            ),
      platelets: make(
        'Blood Clotting & Flow',
        table('platelets'),
      ),
      glucose: make(
        'Glucose Regulation & Metabolic Function',
        table('glucose'),
      ),
      kidney: make(
        'Kidney Function',
        table('kidney'),
      ),
      proteins: make(
        'Protein Status',
        table('proteins'),
      ),
      // Held out of prod on the same flag the analysis page and the PDF use, so
      // the three views of this section appear and disappear together.
      digestion: digestionVisible()
        ? make(
            'Digestive & Gut Health',
            table('digestion'),
          )
        : null,
      nutrients: make(
        'Nutrient Status',
        table('nutrients'),
      ),
      electrolytes: make(
        'Electrolyte Balance',
        // Na/K ratio moved to the Stress & Adrenal section.
        table('electrolytes'),
      ),
      lipids: make(
        'Lipid Balance',
        table('lipids'),
      ),
      inflammation: make(
        'Inflammation & Oxidative Stress',
        table('inflammation'),
      ),
      liver: make(
        'Liver Function',
        table('liver'),
      ),
      thyroid: make(
        'Thyroid Function',
        table('thyroid'),
      ),
      stressAdrenal: make(
        'Stress & Adrenal Balance',
        table('stressAdrenal'),
      ),
      hormones: this.buildHormoneSection(history, gender, isPdf),
      prostate: make(
        'Prostate Function',
        table('prostate'),
      ),
      // Held out of prod on the same flag the analysis page uses, so the
      // section appears and disappears in both places together — the pattern
      // digestion already follows above.
      //
      // Deliberately absent from COMPARISON_SECTION_BY_KEY: that map exists so
      // a custom marker filed under a report Section lands in the matching
      // comparison table, and MVX is not a Section. withPlacedCustomFields
      // returns the field list untouched for a key it cannot map, which is
      // exactly right here — there is no MVX section for a practitioner to
      // file a custom marker into.
      mvx: mvxVisible()
        ? make(
            'Metabolic Vulnerability Index (MVX) Plus',
            table('mvx'),
          )
        : null,
      customMarkers: (() => {
        // Not through table(): the appendix's field list is not a shipped
        // section's plus placements, it IS the leftovers.
        const fields = this.customMarkerComparisonFields(history);
        const textMarkers = this.textMarkersForComparisonKey(
          'customMarkers',
          history,
        );
        return make(
          Section.CUSTOM_MARKERS,
          built(
            this.comparisonTable(history, fields, undefined, textMarkers, {
              sectionKey: 'customMarkers',
              isPdf,
            }),
            fields,
            textMarkers,
          ),
        );
      })(),
    };

    // The practitioner's own sections, one per name in the history, between the
    // shipped panels and the catch-all appendix — the order the analysis screen
    // and the analysis PDF both use.
    this.practitionerSectionNames(history).forEach(name => {
      const key = practitionerSectionOptionValue(name);
      const fields = this.customFieldsForPractitionerSection(name, history);
      const textMarkers = this.textMarkersForComparisonKey(key, history);
      sections[key] = make(
        practitionerSectionTitle(name),
        built(
          this.comparisonTable(history, fields, undefined, textMarkers, {
            sectionKey: key,
            isPdf,
          }),
          fields,
          textMarkers,
        ),
      );
    });

    const order = [
      ...this.comparisonSectionOrder().filter(key => key !== 'customMarkers'),
      ...this.practitionerSectionNames(history).map(
        practitionerSectionOptionValue,
      ),
      'customMarkers',
    ];

    return order
      .map(key => {
        const s = sections[key];
        return s
          ? {
              key,
              title: s.title,
              table: s.table,
              fields: s.fields,
              textMarkers: s.textMarkers,
            }
          : null;
      })
      .filter(Boolean) as {
      key: string;
      title: string;
      table: React.ReactNode;
      fields?: PanelField[];
      textMarkers?: any[];
    }[];
  }

  /**
   * Blood Clotting & Flow comparison field list. Always the coagulation
   * markers; the two viscosity inputs (Hematocrit, then Total Protein) are
   * appended ONLY when the viscosity estimate is present in the history — which
   * requires both inputs in the same test — so a lone HCT/protein row never
   * shows up under coagulation. Shared by the screen table, the PDF, and the
   * PDF height estimate so all three agree.
   */
  /**
   * The PanelField list for the "Other Lab Results" comparison section.
   *
   * Custom markers are per-test, so unlike every other section this list is not
   * a static PanelField array — it is the union of the custom markers across the
   * whole history, in first-appearance order, keyed on the name-derived
   * `custom:` id so the same marker name typed on two tests lines up on one row.
   * The label comes from the MOST RECENT test carrying the key, so a corrected
   * spelling wins.
   *
   * The synthesized fields are the same objects the analysis screen charts with
   * (BloodworkAnalysis.customPanelMarkers), so comparisonTable resolves rows via
   * findPanelMarker and reads the range columns straight off them.
   *
   * Caveat, inherent to per-test ranges: the range columns come from the LAST
   * analysis in the history only (see the Alarm/Lab/Optimal cells in
   * comparisonTable), so a marker absent from the newest test shows blank range
   * columns; and if the practitioner typed different ranges on each test, the
   * trend thumb compares percentFromMean values scored against different range
   * definitions.
   */
  /**
   * The PanelMarker whose range fills a comparison row's six range columns:
   * the newest test that actually has one, searching backwards.
   *
   * Shipped markers resolve a range on every test from the global catalog, so
   * this returns `preferred` (the newest analysis's marker) on the first check.
   * It earns its keep for CUSTOM markers, whose ranges live on the individual
   * test they were typed into — without it, a custom marker entered on an older
   * test and omitted from the newest renders with every range column blank.
   */
  private findRangeBearingPanelMarker(
    field: PanelField,
    history: BloodworkAnalysis[],
    preferred?: PanelMarker,
  ): PanelMarker | undefined {
    if (preferred?.range) {
      return preferred;
    }
    // Scoped to custom markers on purpose. A SHIPPED marker resolves its range
    // from the global catalog on every test, so it only lands here when
    // findRange matched nothing — and an older test can match a different
    // age/menopause/trimester band, which would put a range in these columns
    // that does not belong to the newest test. Leaving those blank is the
    // long-standing behavior and stays untouched.
    if (!CustomMarker.isCustomKey(field.id)) {
      return preferred;
    }
    for (let i = history.length - 1; i >= 0; i--) {
      const panelMarker = this.findPanelMarker(field, history[i]);
      if (panelMarker?.range) {
        return panelMarker;
      }
    }
    return preferred;
  }

  private customMarkerComparisonFields(
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (!field || !field.id || byId.has(field.id)) {
          return;
        }
        byId.set(field.id, field);
      });
    });
    // Markers filed under a body system are shown there instead — but only when
    // that section has results of its own somewhere in the history, mirroring
    // BloodworkAnalysis.withCustomMarkers()'s empty-target guard. Otherwise they
    // come back here, so a placed marker is never lost between the two.
    //
    // A marker filed under a section the practitioner NAMED is shown there
    // unconditionally: that section exists because they made it, so there is no
    // empty target to guard against.
    return Array.from(byId.values()).filter(
      field =>
        !this.isPlacedInSection(field, history) && !field.practitionerSection,
    );
  }

  /**
   * The practitioner-named sections present anywhere in the history, in My
   * Sections' order, each carrying the name as it was typed.
   *
   * Gathered from the tests rather than from that list, for the reason
   * orderPractitionerSectionNames gives: a comparison reaches back through
   * tests saved under sections since renamed or deleted, and only the tests
   * know what was on them. The list decides which of the sections found leads;
   * one it does not name keeps its first-appearance place behind the ones it
   * does.
   *
   * Deduplicated on the normalized name so one panel typed "IgG Allergens" in
   * March and "IGG allergens" in June is one section with one set of rows,
   * rather than two sections that each look half-empty.
   */
  private practitionerSectionNames(history: BloodworkAnalysis[]): string[] {
    const byKey = new Map<string, string>();
    history.forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const name = (panelMarker.field?.practitionerSection || '').trim();
        if (!name) {
          return;
        }
        const key = practitionerSectionKey(name);
        // Later tests win the spelling, the first one wins the position.
        byKey.set(key, name);
      });
      // Charted markers first, then the text ones — a section can be made
      // entirely of text results (a urinalysis panel is), and without this it
      // would have no heading in the comparison report while having one on the
      // analysis screen and in the PDF.
      (bwa.customTextMarkers || []).forEach(textMarker => {
        const name = (textMarker.practitionerSection || '').trim();
        if (!name || !textMarker.hasText()) {
          return;
        }
        byKey.set(practitionerSectionKey(name), name);
      });
    });
    // The screen table and the comparison PDF are both built from
    // buildComparisonSections, which calls this, so ordering here orders both.
    //
    // The NEWEST test's arrangement leads, which is the rule this table already
    // follows for its rows (newestFirst, first-seen-wins): one table cannot
    // honour four tests' arrangements, and the newest is the one the
    // practitioner arranged most recently. History is oldest-first here, so the
    // newest is the last entry.
    return orderPractitionerSectionNames(
      Array.from(byKey.values()),
      sectionRankingFor(
        history[history.length - 1]?.bloodwork?.customMarkerSectionOrder,
        this.props.currentUserProfile?.customMarkerSections,
      ),
    );
  }

  /** The comparison fields for one practitioner-named section. */
  private customFieldsForPractitionerSection(
    name: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const key = practitionerSectionKey(name);
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (
          field &&
          field.id &&
          practitionerSectionKey(field.practitionerSection) === key &&
          !byId.has(field.id)
        ) {
          byId.set(field.id, field);
        }
      });
    });
    return Array.from(byId.values());
  }

  /**
   * Every custom marker in the history filed under `section`, deduplicated the
   * same way customMarkerComparisonFields deduplicates: by the name-derived
   * `custom:` id, newest label winning.
   */
  private customFieldsForSection(
    section: Section,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const byId = new Map<string, PanelField>();
    newestFirst(history).forEach(bwa => {
      (bwa.customPanelMarkers || []).forEach(panelMarker => {
        const field = panelMarker.field;
        if (
          field &&
          field.id &&
          field.customSection === section &&
          !byId.has(field.id)
        ) {
          byId.set(field.id, field);
        }
      });
    });
    return Array.from(byId.values());
  }

  /**
   * Every text-result marker anywhere in the history, deduplicated by name.
   *
   * The text counterpart of customMarkerComparisonFields, and deduplicated the
   * same way: keyed on the name-derived `custom:` id, so a urinalysis typed on
   * three tests is one row with three cells rather than three rows. Later tests
   * win the label and the expectation (a corrected spelling should show, and
   * the newest expectation is the one the practitioner currently holds), the
   * first appearance wins the position.
   *
   * Read from each analysis's OWN markers — hasText() filters out the
   * previous-only stand-ins it built for the analysis screen, which here would
   * be the previous test's results counted twice.
   */
  private textMarkersInHistory(
    history: BloodworkAnalysis[],
  ): CustomTextMarker[] {
    const byKey = new Map<string, CustomTextMarker>();
    newestFirst(history).forEach(bwa => {
      (bwa.customTextMarkers || []).forEach(textMarker => {
        // First seen wins outright, and newest-first means that is the newest
        // test carrying it: its position, its wording, its section. The older
        // tests' copies of the same marker say nothing this one does not.
        if (!textMarker.hasText() || byKey.has(textMarker.key)) {
          return;
        }
        // The per-test words are read separately (textValueFor), so this
        // carries the marker's identity only.
        byKey.set(
          textMarker.key,
          new CustomTextMarker({
            ...textMarker,
            text: undefined,
            previousText: undefined,
          }),
        );
      });
    });
    return Array.from(byKey.values());
  }

  /** One test's words for a text marker, or '' when it did not carry it. */
  private textValueFor(
    textMarker: CustomTextMarker,
    bwa: BloodworkAnalysis,
  ): string {
    const found = (bwa.customTextMarkers || []).find(
      entry => entry.key === textMarker.key && entry.hasText(),
    );
    return found ? found.text!.trim() : '';
  }

  /**
   * Whether a text marker's chosen shipped section has results of its own
   * anywhere in the history — the same empty-target guard isPlacedInSection
   * applies to charted markers, asked of the same section field.
   */
  private isTextPlacedInSection(
    textMarker: CustomTextMarker,
    history: BloodworkAnalysis[],
  ): boolean {
    if (!textMarker.section) {
      return false;
    }
    const key = Object.keys(COMPARISON_SECTION_BY_KEY).find(
      k => COMPARISON_SECTION_BY_KEY[k] === textMarker.section,
    );
    if (!key) {
      return false;
    }
    return this.shippedFieldsForComparisonKey(key, history).some(shipped =>
      this.fieldHasAnyValue(shipped, history),
    );
  }

  /**
   * The text markers belonging to one comparison section key.
   *
   * Mirrors shippedFieldsForComparisonKey's three-way split so a marker lands
   * in exactly one section here, in the section the analysis screen and the PDF
   * put it in.
   */
  private textMarkersForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): CustomTextMarker[] {
    const all = this.textMarkersInHistory(history);
    const practitionerName = practitionerSectionFromOptionValue(key);
    if (practitionerName) {
      const wanted = practitionerSectionKey(practitionerName);
      return all.filter(
        textMarker =>
          !this.isTextPlacedInSection(textMarker, history) &&
          practitionerSectionKey(textMarker.practitionerSection) === wanted,
      );
    }
    if (key === 'customMarkers') {
      return all.filter(
        textMarker =>
          !this.isTextPlacedInSection(textMarker, history) &&
          !textMarker.practitionerSection,
      );
    }
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return [];
    }
    return all.filter(
      textMarker =>
        textMarker.section === section &&
        this.isTextPlacedInSection(textMarker, history),
    );
  }

  /** Whether a custom field's chosen section has results of its own to sit with. */
  private isPlacedInSection(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    if (!field.customSection) {
      return false;
    }
    const key = Object.keys(COMPARISON_SECTION_BY_KEY).find(
      k => COMPARISON_SECTION_BY_KEY[k] === field.customSection,
    );
    if (!key) {
      return false;
    }
    return this.shippedFieldsForComparisonKey(key, history).some(shipped =>
      this.fieldHasAnyValue(shipped, history),
    );
  }

  /**
   * A section's own comparison fields, plus any custom markers filed under it.
   *
   * Wrapped around every field list the comparison renders AND the list its PDF
   * height estimate counts, so the table, the page-break decision and the
   * analysis page all agree on where a custom marker lives.
   */
  private withPlacedCustomFields(
    key: string,
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return fields;
    }
    // Empty-target guard, same as the analysis page: a section with nothing of
    // its own is not brought to life by a custom marker.
    if (!fields.some(field => this.fieldHasAnyValue(field, history))) {
      return fields;
    }
    const placed = this.customFieldsForSection(section, history);
    return placed.length ? [...fields, ...placed] : fields;
  }

  private plateletsComparisonFields(
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const viscosity = PanelField.findField(
      PanelField.PLATELETS_FIELDS,
      'viscosityCapiliaries',
    );
    const showInputs =
      !!viscosity && this.fieldHasAnyValue(viscosity, history);
    return PanelField.plateletsComparisonFields(showInputs);
  }

  /**
   * The PanelField list backing each comparison section key, mirroring the
   * field sets used in buildComparisonSections. Used to estimate a section's
   * rendered height (via countVisibleFields) so the PDF renderer can decide
   * whether a section is tall enough to need its own fresh page.
   */
  private fieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    return this.withPlacedCustomFields(
      key,
      this.shippedFieldsForComparisonKey(key, history),
      history,
    );
  }

  /**
   * The section's own fields, before any practitioner-added markers are placed
   * into it. Kept separate so the placement guard has something to ask "does
   * this section have results of its own?" without recursing.
   *
   * = the section's declared field list PLUS anything the analysis page
   * actually charts under it that the list doesn't name (see
   * chartedFieldsForComparisonKey). A marker on the report and not in the
   * comparison table is the failure this exists to make impossible.
   */
  private shippedFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    return this.withChartedSectionFields(
      key,
      this.declaredFieldsForComparisonKey(key, history),
      history,
    );
  }

  /**
   * `fields` plus anything the analysis page charts under this section that
   * they don't already name. Separate from shippedFieldsForComparisonKey so the
   * Hormone Balance section — which builds its own list twice, for the screen
   * and for the PDF's page chunking — can top itself up the same way.
   */
  private withChartedSectionFields(
    key: string,
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const present = new Set(fields.map(field => field.id));
    const charted = this.chartedFieldsForComparisonKey(key, history).filter(
      field => !present.has(field.id),
    );
    return charted.length ? [...fields, ...charted] : fields;
  }

  /**
   * Fields the ANALYSIS page charts under this section somewhere in the
   * history, in the order its panel getter returns them.
   *
   * Sections borrow markers from each other — the Immune panel charts zinc, the
   * Inflammation panel charts ferritin/albumin/GGT and eight more, and Glucose
   * pulls in LDL, ALT, cortisol and the rest of its add-ons when a pattern
   * flags them. Those markers were on the analysis report and in the client's
   * PDF, but the comparison table listed only each section's own declared
   * fields, so a practitioner comparing two tests lost them from the section
   * they had just read them in. (They still appeared under their home section,
   * which is why this read as an ordering quirk rather than missing data.)
   *
   * Derived from the getter rather than a hand-written second list, so a marker
   * added to a section's panel reaches the comparison table without a matching
   * edit here — and so the conditional add-ons appear exactly when the analysis
   * page shows them, not on every test that happens to carry an LDL.
   *
   * Practitioner-typed markers are excluded: withPlacedCustomFields already
   * places those, and it applies an empty-target guard this must not bypass.
   */
  private chartedFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const section = COMPARISON_SECTION_BY_KEY[key];
    if (!section) {
      return [];
    }
    const getterName = SECTION_PANEL_MARKER_GETTERS[section];
    if (!getterName) {
      return [];
    }
    const seen = new Set<string>();
    const fields: PanelField[] = [];
    history.forEach(bwa => {
      const getter = (bwa as any)[getterName];
      if (typeof getter !== 'function') {
        return;
      }
      (getter.call(bwa) || []).forEach((panelMarker: PanelMarker) => {
        const field = panelMarker && panelMarker.field;
        if (!field || CustomMarker.isCustomKey(panelMarker.attribute)) {
          return;
        }
        if (seen.has(field.id)) {
          return;
        }
        seen.add(field.id);
        fields.push(field);
      });
    });
    return fields;
  }

  /**
   * The field list each section DECLARES — the static arrays the sections were
   * built from. shippedFieldsForComparisonKey tops this up with whatever the
   * analysis page charts beyond it.
   */
  private declaredFieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    switch (key) {
      case 'rbc':
        return [...PanelField.RBC_FIELDS];
      case 'iron':
        return [...PanelField.IRON_FIELDS, ...PanelField.COPPER_FIELDS];
      case 'immune':
        return [
          ...PanelField.IMMUNE_SYSTEM_FIELDS,
          ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
        ];
      case 'platelets':
        return this.plateletsComparisonFields(history);
      case 'glucose':
        // Order lives in PanelField.COMPARISON_GLUCOSE_FIELDS, which explains why
        // amylase and leptin sit after the lipid trio.
        return [...PanelField.COMPARISON_GLUCOSE_FIELDS];
      case 'mvx':
        // All nine: the three NMR index scores and the six measured factors.
        return [...PanelField.MVX_FIELDS];
      case 'kidney':
        return [...PanelField.KIDNEY_FIELDS];
      case 'proteins':
        return [...PanelField.PROTEINS_FIELDS];
      case 'digestion':
        // Resolved from ALL_FIELDS rather than a PanelField array of its own:
        // every digestion marker is borrowed from another panel, so the ids are
        // the list and the field definitions stay where they already live.
        // Order comes from DIGESTION_FIELD_IDS, which prints the same order the
        // analysis page and the PDF do.
        return DIGESTION_FIELD_IDS.map(id =>
          PanelField.findField(PanelField.ALL_FIELDS, id),
        ).filter(Boolean) as PanelField[];
      case 'nutrients':
        return [...PanelField.NUTRIENTS_FIELDS, ...PanelField.ZINC_FIELDS];
      case 'electrolytes':
        return PanelField.ELECTROLYTES_FIELDS.filter(f => f.id !== 'sodiumPotassiumRatio');
      case 'stressAdrenal':
        return [...PanelField.STRESS_ADRENAL_FIELDS];
      case 'lipids':
        return [...PanelField.COMPARISON_LIPIDS_FIELDS];
      case 'inflammation':
        return [...PanelField.COMPARISON_INFLAMMATION_FIELDS];
      case 'liver':
        return [...PanelField.LIVER_FIELDS];
      case 'thyroid':
        return [...PanelField.THYROID_FIELDS];
      case 'hormones':
        return [...PanelField.HORMONE_FIELDS_1, ...PanelField.HORMONE_FIELDS_2].filter(
          f => !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id),
        );
      case 'prostate':
        return [...PanelField.PROSTATE_FIELDS];
      case 'customMarkers':
        return this.customMarkerComparisonFields(history);
      default: {
        // The practitioner's own sections are keyed by name rather than by a
        // fixed key, since there is no fixed list of them.
        const name = practitionerSectionFromOptionValue(key);
        return name ? this.customFieldsForPractitionerSection(name, history) : [];
      }
    }
  }

  /**
   * Renders the PDF sections in the test-results analysis order — the one and
   * only comparison order (the old lab-report order was retired).
   *
   * Page-break strategy (works WITH html2pdf 0.9.1's actual engine, not against
   * it — see node_modules/html2pdf.js/src/plugin/pagebreaks.js):
   *
   *  1. Every section's title + table is wrapped in a `.pdf-section` /
   *     `.section-block` pair carrying `page-break-inside: avoid`. For any
   *     section that fits within a single page, html2pdf moves the WHOLE block
   *     to the next page when it would otherwise straddle a boundary — so the
   *     title can never be stranded at the bottom of a page with its table on
   *     the next (the "Kidney Function" orphan bug). `break-after/before: avoid`
   *     would be cleaner but html2pdf silently ignores them; `inside: avoid` is
   *     the only keep-together primitive it honors.
   *
   *  2. html2pdf only applies that whole-block push when the block is <= 1 page
   *     tall; a taller section is left to break naturally, which could again
   *     orphan its title if it happens to start near a page bottom. To preserve
   *     the no-orphan guarantee for EVERY combination of sections, any section
   *     whose visible-row count could approach/exceed one page is forced onto a
   *     fresh page (title lands at the top; only the table body — never the
   *     title — spills across the page boundary).
   */
  private renderPdfSectionsAnalysisOrder(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
  ): React.ReactNode {
    // Sections that always begin on a fresh page (heavy and/or grouped this way
    // historically). Glucose has long been a hard page-start in this report.
    // customMarkers is always last and its row count is unbounded (up to 20 per
    // test), so it can't be safely packed against a neighbour either.
    //
    // Hormone Balance came OUT of this set on 2026-09-24. It was here for the
    // historical reason rather than a structural one, and it costs a page every
    // time: a ten-row hormone panel under a five-row Stress & Adrenal is half a
    // page of print and two pages of paper. It paginates itself when it is long
    // (renderHormoneSectionPdf), and when it is short it now packs like any
    // other section.
    const alwaysBreakBefore = new Set([
      'glucose',
      'prostate',
      'customMarkers',
    ]);

    // A section this tall or more is treated as "tall" (could fill or exceed a
    // landscape page) and is started on its own page so its title is guaranteed
    // to land at the top, never orphaned. Kept safely below the 24 one-line
    // rows a landscape page holds.
    //
    // Measured in the page budget's own units rather than in rows, because a
    // row is not a fixed height: a urinalysis result that wraps to two lines is
    // two thirds taller than "Negative", and counting both as one row is how
    // sixteen rows of words were read as two thirds of a page when they were a
    // page and a half of print.
    const TALL_SECTION_COST = 12 * COMPARISON_ROW_COST;

    const sections = this.buildComparisonSections(
      history,
      gender,
      whiteBloodCellsHasValues,
      true,
    );
    // Precompute each section's height class and whether it opens a fresh page,
    // so the "lead the page" rule below can run before we render.
    const meta = sections.map((section, idx) => {
      const fieldCount = this.countVisibleFields(
        this.fieldsForComparisonKey(section.key, history),
        history,
      );
      // Text markers render as rows like any other, and each one is as tall as
      // the lines its longest result wraps onto — the same measure the chunker
      // cuts pages with, so a section cannot be one height to the rule that
      // places it and another to the rule that splits it.
      const textRowLines = this.textRowLinesFor(
        this.textMarkersForComparisonKey(section.key, history),
        history,
      );
      // The Notes cell spans the section's rows, so it makes the table taller
      // only when it outruns them. Counted only when the column is actually
      // printing: the same two conditions the column itself is drawn under (the
      // with-notes export, and this report's Include checkbox over the Report
      // Setting). Reading the export flag alone reserved height for a column
      // that was switched off, which broke sections onto pages they did not
      // need.
      const noteRows =
        this.state.withNotes && this.notesColumnInPdfEnabled()
          ? noteRowsUsed(this.noteBlocksFor(section.key, history).shown || '')
          : 0;
      const cost = comparisonSectionCost(fieldCount, textRowLines, noteRows);
      const isTall = cost >= TALL_SECTION_COST;
      const forcesFreshPage =
        idx > 0 && (alwaysBreakBefore.has(section.key) || isTall);
      return { section, isTall, forcesFreshPage, cost };
    });

    // Whether each section emits a page break before it. Seeded from
    // forcesFreshPage, then adjusted by the lead-the-page rule below.
    const startsFreshPage = meta.map(m => m.forcesFreshPage);

    // Lead-the-page rule: a SHORT section sandwiched between a tall section
    // (which fills/spills its own page) and a following tall section (which
    // would force its own fresh page) gets stranded alone — html2pdf bumps the
    // short block off the spill page via page-break-inside:avoid, and the next
    // tall section then opens a brand-new page below it. This is the reported
    // "Protein Status alone on a page, Immune Activity on the next page" bug.
    // Move the break to BEFORE the short section so it LEADS the fresh page the
    // tall section was going to open, and let the tall section flow directly
    // beneath it (Protein + Immune share a page; everything after moves up).
    // Only applies when the next section forces a page purely by height — never
    // the intentional hard-break sections (Glucose/Hormones/Prostate).
    for (let i = 1; i < meta.length - 1; i++) {
      const prev = meta[i - 1];
      const cur = meta[i];
      const next = meta[i + 1];
      const nextForcedByHeight =
        next.forcesFreshPage && !alwaysBreakBefore.has(next.section.key);
      /*
       * ...but only when the tall section FITS under the short one.
       *
       * Without the fit test this rule is what put Liver Function's last rows
       * on a page of their own with no heading over them: 16 rows flowing
       * beneath a short section, on a page with room for a dozen. Packing two
       * sections onto a page is worth doing when they both fit and is a
       * spillover when they do not, and the row counts to tell them apart are
       * already computed above.
       */
      // What is left of the page once the short section AND the tall section's
      // own heading and column header are on it. Leaving the chrome out is what
      // put four Liver Function rows at the top of the next page with nothing
      // over them: 11 rows and 12 rows read as 23 of an apparent 24.
      const roomLeft =
        COMPARISON_PAGE_BUDGET - cur.cost - COMPARISON_SECTION_BLOCK_COST;
      if (
        !cur.forcesFreshPage
        && prev.isTall
        && nextForcedByHeight
        && next.cost <= roomLeft
      ) {
        startsFreshPage[i] = true; // short section leads the fresh page
        startsFreshPage[i + 1] = false; // tall section flows beneath it
      }
    }

    return (
      <>
        {meta.map(({ section, isTall }, idx) => {
          const needsFreshPage = startsFreshPage[idx];
          // Hormone Balance has ~29 possible markers and can run several rows
          // past a single page. Render it as page-sized chunks, each with its
          // own column header and a repeated "Hormone Balance (cont.)" heading,
          // instead of letting one oversized table flow (and get sliced
          // mid-row) across the page boundary.
          if (section.key === 'hormones') {
            return (
              <React.Fragment key={section.key}>
                {this.renderHormoneSectionPdf(history, gender, needsFreshPage)}
              </React.Fragment>
            );
          }
          /*
           * A section longer than one page is split here, not left to flow.
           *
           * Flowing is what produced the reported bug: the remainder of Liver
           * Function, and four fifths of a 38-row Urinalysis, continued at the
           * top of the next page with no heading and no column header, so a
           * reader meeting that page had nothing telling them what the numbers
           * were. Each chunk is now a page of rows with its own table, under a
           * heading that repeats as "Urinalysis (cont.)" — the pattern Hormone
           * Balance, the notes table and the MOO columns already use.
           *
           * Hormone Balance keeps its own renderer above: it also compresses
           * its rows to fit a wider field list, which this does not.
           */
          const rowInputs =
            section.fields && section.textMarkers
              ? { fields: section.fields, textMarkers: section.textMarkers }
              : undefined;
          if (
            rowInputs
            && comparisonSectionNeedsChunking(
              this.countVisibleFields(rowInputs.fields, history),
              this.textRowLinesFor(rowInputs.textMarkers, history),
            )
          ) {
            return (
              <React.Fragment key={section.key}>
                {this.renderChunkedSectionPdf(
                  history,
                  section,
                  rowInputs.fields,
                  rowInputs.textMarkers,
                  idx > 0,
                )}
              </React.Fragment>
            );
          }
          // A section whose title + table is taller than one page cannot be
          // kept together. With the default page-break-inside:avoid on
          // .section-block, html2pdf bumps the whole table to the next page when
          // it would straddle the boundary — which strands the title alone on
          // the forced-fresh page above it. For these tall sections, let the
          // table flow so the title stays glued to its first rows; individual
          // rows stay intact via the `tr` keep-together rule in the style block.
          const flowStyle: React.CSSProperties | undefined = isTall
            ? { pageBreakInside: 'auto', breakInside: 'auto' }
            : undefined;
          return (
            <React.Fragment key={section.key}>
              {needsFreshPage && <div className="html2pdf__page-break" />}
              <div className="pdf-section" style={flowStyle}>
                <div style={{ height: '8px' }} />
                <h5>
                  <strong>{section.title}</strong>
                </h5>
                <div className="section-block" style={flowStyle}>
                  {section.table}
                </div>
                <div style={{ height: '8px' }} />
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /** Count how many fields would render given the current hide/show blanks setting.
   *  - When showHiddenRows === false (Hide blank rows is CHECKED):
   *      Only fields with at least one value in the history are counted.
   *  - When showHiddenRows === true (Hide blank rows is UNCHECKED):
   *      All non-hidden fields are counted, even if they have no values.
   */
  private countVisibleFields(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): number {
    const hideBlanks = !this.state.showHiddenRows;
    const visible = fields.filter(
      f =>
        !f.isHiddenFromComparison &&
        (hideBlanks ? this.fieldHasAnyValue(f, history) : true),
    );
    return visible.length;
  }

  /** Returns true if any of the iron markers in PanelField.IRON_FIELDS
   *  has a value anywhere in the history.
   *
   *  Used for the "hide blank rows" rule:
   *  - When hide blank rows is ON:
   *      If none of these iron markers has a value, the entire
   *      "Iron Related Markers" section is hidden (UI + PDF).
   *  - When hide blank rows is OFF:
   *      The section is always shown, even if all values are blank.
   */
  private anyIronCoreMarkerHasValue(history: BloodworkAnalysis[]): boolean {
    const ironOnlyFields = [...PanelField.IRON_FIELDS];
    return this.sectionHasAnyValues(ironOnlyFields, history);
  }

  // ---------- Logo helpers (preview reliability) ----------

  getLogoUrl = (): string => {
    const { currentUserProfile } = this.props;

    return (
      currentUserProfile?.hasCustomLogo()
        ? currentUserProfile.customLogoUrl
        : (logo as any)
    ) as any;
  };

  /** Normalize whatever we store (URL, key, filename) into an S3 key if possible. */
  private extractUserLogoKey = (candidate: string): string | undefined => {
    if (!candidate) return undefined;

    // Already a key like "user-logos/abc.png"
    if (/^user-logos\//i.test(candidate)) {
      return candidate.replace(/^\/+/, '');
    }

    // "public/user-logos/..." (Amplify storage path)
    const publicIdx = candidate.indexOf('public/user-logos/');
    if (publicIdx >= 0) {
      return candidate.slice(publicIdx + 'public/'.length);
    }

    // Any URL that contains "/user-logos/..."
    const seg = '/user-logos/';
    const idx = candidate.indexOf(seg);
    if (idx >= 0) {
      return candidate.slice(idx + 1); // drop the leading '/'
    }

    // Bare filename? assume user-logos/<filename>
    const isUrl = /^https?:\/\//i.test(candidate) || /^data:/i.test(candidate);
    if (!isUrl) {
      const fn = candidate.split('/').pop();
      if (fn) return `user-logos/${fn}`;
    }

    return undefined;
  };

  /**
   * Convert any URL to a data URL for reliable rendering.
   *
   * Shared with both export paths (see utils/imageDataUrl): what comes back is
   * checked to be an image, so a fetch that quietly returns nothing falls
   * through to the S3-signed branch below instead of being cached as the logo.
   */
  private toDataUrl = (url: string): Promise<string> => toImageDataUrl(url);

  /**
   * Which logo the cover page should be showing right now: the practitioner's
   * custom one when their profile allows it, else the bundled LabSmarts mark.
   * Doubles as the cache key for the resolved data URL.
   */
  private desiredLogoKey = (): string => {
    const { currentUserProfile } = this.props;
    return currentUserProfile?.hasCustomLogo()
      ? currentUserProfile.customLogoUrl
      : BUNDLED_LOGO_KEY;
  };

  /**
   * Re-resolve the cover logo when the profile it came from has changed.
   *
   * componentDidMount alone is too early. React mounts children before their
   * parent, so this component asks for the logo BEFORE
   * bloodwork-analysis-container has even started loading the profile:
   * hasCustomLogo() is false at that moment, so the first resolution always
   * lands on the bundled mark. Without this the comparison kept it for the rest
   * of the session and the export printed the LabSmarts logo on a
   * practitioner's branded report.
   *
   * Keyed on the logo URL rather than on the profile's object identity because
   * the store mutates the profile in place when a new logo is uploaded.
   */
  private syncHeaderPreviewLogo = () => {
    const key = this.desiredLogoKey();
    if (key === this.state.headerLogoKey || key === this.resolvingLogoKey) {
      return;
    }
    this.ensureHeaderPreviewLogo();
  };

  /** Ensure the preview <Image id="logo"> gets a real, loadable data URL. */
  private ensureHeaderPreviewLogo = async () => {
    const { currentUserProfile } = this.props;

    // Fallback bundled asset (always resolvable)
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // The logo this pass is resolving. Held on the instance so the renders that
    // happen while the fetch is in flight don't start the same work again, and
    // checked again on the way out so a pass that was overtaken (the profile
    // arrived, or the practitioner uploaded a new logo) cannot overwrite the
    // newer result.
    const key = this.desiredLogoKey();
    this.resolvingLogoKey = key;

    const apply = (src: string) => {
      if (this.desiredLogoKey() !== key) return;
      this.resolvingLogoKey = undefined;
      this.setState({ headerLogoSrc: src, headerLogoKey: key });
    };

    // No custom logo: use bundled default directly, skip all S3 logic
    if (!currentUserProfile?.hasCustomLogo()) {
      try {
        const dataUrl = await this.toDataUrl(bundled);
        apply(dataUrl);
      } catch {
        apply(bundled);
      }
      return;
    }

    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // If full URL, convert to data URL
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        const dataUrl = await this.toDataUrl(candidate);
        apply(dataUrl);
        return;
      } catch {
        // fall through to S3/bundled
      }
    }

    // If already a data URL, use it directly
    if (candidate && /^data:/i.test(candidate)) {
      apply(candidate);
      return;
    }

    // Try to sign via Storage.get and convert to data URL
    try {
      const storageKey = this.extractUserLogoKey(candidate);
      if (storageKey) {
        const signed = (await Storage.get(storageKey)) as string;
        const dataUrl = await this.toDataUrl(signed);
        apply(dataUrl);
        return;
      }
    } catch {
      // fall through
    }

    // Fallback to bundled (convert to data URL). Recorded against the custom
    // logo that failed, so a logo S3 will not serve is fetched once rather than
    // on every render; a later profile change still re-triggers the resolution.
    try {
      const dataUrl = await this.toDataUrl(bundled);
      apply(dataUrl);
    } catch {
      apply(bundled);
    }
  };

  // -------------------------------------------------------

  componentDidMount() {
    // Make sure modal preview shows a loadable logo URL
    this.ensureHeaderPreviewLogo();

    // Seed the Section Notes column from the practitioner's last choice. It
    // lives in localStorage rather than the profile so it needs no schema
    // change; unset means the default (shown).
    const key = this.sectionNotesStorageKey();
    if (key && localStorage.getItem(key) === 'false') {
      this.setState({ showSectionNotes: false });
    }

    this.props.furtherConsiderationsEntryStore
      ?.loadFurtherConsiderations()
      .then(furtherConsiderations => {
        this.setState({ furtherConsiderations });
      });
  }

  /** Per-user key for the Section Notes column preference. */
  private sectionNotesStorageKey(): string | null {
    const id = this.props.currentUserProfile?.id;
    return id ? `${id}-comparison-show-section-notes` : null;
  }

  /** Show/hide the Section Notes column (applies to the PDF export too). */
  private handleShowSectionNotesChange = (checked: boolean) => {
    this.setState({ showSectionNotes: checked });
    const key = this.sectionNotesStorageKey();
    if (key) localStorage.setItem(key, String(checked));
  };

  findPanelMarker(
    panelField: PanelField,
    bloodworkAnalysis: BloodworkAnalysis,
  ): PanelMarker {
    return bloodworkAnalysis.findPanelMarker(panelField.id);
  }

  displayValue(panelMarker: PanelMarker) {
    if (panelMarker) {
      return panelMarker.hasValue()
        ? MathUtils.round(panelMarker.value, panelMarker.field.precision)
        : null;
    } else {
      return 'N/A';
    }
  }

  getClassName(pm: PanelMarker): string {
    if (!pm || pm.isBlank() || pm.isOptimal()) {
      return '';
    }

    if (pm.field.noRedChart) {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (!pm.isLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.noRedOrangeChart) {
      if (pm.isNotOptimal() && pm.isLessThanOrIsLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.useRDWChart) {
      if (pm.isGreaterThanOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isNotLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (!pm.isAlarm()) {
        return 'bg-red';
      }
    }
    return '';
  }

  getIcon(pm: PanelMarker) {
    if (pm.isBlank()) return null;
    let img: any;
    if (pm.isGreaterThanOptimal()) {
      img = arrow_up;
    } else if (pm.isStricklyLessThanOptimal()) {
      img = arrow_down;
    }
    return <img src={img} alt="" />;
  }

  comparisonDetails(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    pm: PanelMarker,
    index: number,
  ) {
    if (!pm) {
      return null;
    }

    if (pm.field.id === 'menstrualPhase') {
      return (
        <td>
          {pm.altValue === MenstrualPhase.NONE ? (
            <>
              {MenstrualPhase.NONE}
              <br />
              (children&apos;s ranges)
            </>
          ) : (
            pm.altValue
          )}
        </td>
      );
    }

    let img, imgSrc;
    const valueDetail = (
      <td className={this.getClassName(pm)} key={index}>
        {this.displayValue(pm)}&nbsp;&nbsp;{this.getIcon(pm)}
      </td>
    );
    if (index + 1 < bloodworkAnalysisHistory.length) {
      return valueDetail;
    } else {
      if (
        pm &&
        pm.hasValue() &&
        pm.previousPanelMarker &&
        pm.previousPanelMarker.hasValue()
      ) {
        if (pm.isOptimal() && pm.previousPanelMarker.isOptimal()) {
          // No op
        } else {
          if (
            Math.abs(pm.percentFromMean) <
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_up;
          } else if (
            Math.abs(pm.percentFromMean) >
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_down;
          }
          img = <img src={imgSrc} alt="" />;
        }
      }

      return (
        <>
          {valueDetail}
          <td style={{ textAlign: 'center' }}>{img}</td>
          <td>{pm.range?.alarmMin}</td>
          <td>{pm.range?.labMin}</td>
          <td>{pm.range?.optimalMin}</td>
          <td>{pm.range?.optimalMax}</td>
          <td>{pm.range?.labMax}</td>
          <td>{pm.range?.alarmMax}</td>
        </>
      );
    }
  }

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  hideBlankRowsTooltip() {
    return (
      <Tooltip id="hide-blank-rows-tooltip">
        <div>
          When checked, marker rows with no recorded value across every
          collection are hidden, and sections with no values drop out entirely.
          Uncheck to show all markers, including blanks.
        </div>
      </Tooltip>
    );
  }

  hideAlarmTooltip() {
    return (
      <Tooltip id="hide-alarm-tooltip">
        <div>
          When checked, the Alarm Min and Alarm Max columns are hidden. The
          PDF export follows this setting. To start every comparison with them
          hidden, check &quot;Hide Alarm Min and Alarm Max&quot; under
          Comparison Table Columns in Report Settings.
        </div>
      </Tooltip>
    );
  }

  hideLabTooltip() {
    return (
      <Tooltip id="hide-lab-tooltip">
        <div>
          When checked, the Lab Min and Lab Max columns are hidden. The PDF
          export follows this setting. To start every comparison with them
          hidden, check &quot;Hide Lab Min and Lab Max&quot; under Comparison
          Table Columns in Report Settings.
        </div>
      </Tooltip>
    );
  }

  showSectionNotesTooltip() {
    return (
      <Tooltip id="show-section-notes-tooltip">
        <div>
          Shows each test's Section Notes (the notes typed on the individual
          section tabs) in their own column beside the Insights note, which
          summarises the whole test. Applies to the PDF export as well. Edit on
          a row changes both, and what you type in Section Notes is kept on that
          test until you reset it.
        </div>
      </Tooltip>
    );
  }

  /** A thin vertical rule used to separate groups in the card header. */
  headerDivider() {
    return (
      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          alignSelf: 'center',
        }}
      />
    );
  }

  export(withNotes = false) {
    this.props.onExportToPdf();
    if (this.props.currentUserProfile.pdfExportComparisonTableAllowed) {
      this.setState({
        showPdfModal: true,
        numResultsToCompare: NUM_RESULTS_TO_COMPARE_FOR_PDF,
        withNotes,
      });
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  exportJson = () => {
    const { bloodworks, bloodworkRanges, clientEntryStore, currentUserProfile } =
      this.props;
    const { furtherConsiderations } = this.state;

    // "Overall Practitioner Notes" is a client-level field — read it from the LIVE
    // client (store), NOT the per-test snapshot, so a practitioner edit/delete is
    // reflected. All tests share one clientId, so bloodworks[0] is sufficient.
    const clientId = bloodworks && bloodworks[0] ? bloodworks[0].clientId : undefined;
    const liveClient =
      clientEntryStore?.clients?.find(c => c.id === clientId) ||
      (bloodworks && bloodworks[0] ? bloodworks[0].client : undefined);

    downloadRawDataExport({
      bloodworks,
      bloodworkRanges,
      furtherConsiderations,
      overallPractitionerNotes: liveClient?.overallPractitionerNotes,
      comparisonSectionNotes: liveClient?.comparisonSectionNotes,
      enabledSupplementBrands: currentUserProfile?.enabledSupplementBrands,
      // Every test the client has, named for the latest collection date.
      filenameStem: 'blood_work_raw_data',
    });
  };

  comparisonTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    panelFields: PanelField[],
    clientGender?: string, //For menstrual phase - not showing if gender is male
    /**
     * This section's practitioner-typed markers whose result is words.
     *
     * Rendered as rows beneath the charted ones, in the same table, for the
     * same reason the analysis screen puts them in the same panel: a section is
     * one table however its markers happen to be reported.
     */
    textMarkers: CustomTextMarker[] = [],
    opts: ComparisonTableOptions = {},
  ) {
    // Determine which rows to show
    const hideBlanks = !this.state.showHiddenRows; // checkbox checked = hide blanks
    // Each checkbox checked = drop its pair of columns; Optimal always stays.
    const showAlarm = !this.alarmColumnsHidden();
    const showLab = !this.labColumnsHidden();
    // How many range columns a text-marker row spans beneath.
    const rangeColumnCount = 2 + (showAlarm ? 2 : 0) + (showLab ? 2 : 0);

    const visibleFields = panelFields.filter((field: PanelField) => {
      if (field.isHiddenFromComparison) return false;

      // Never show menstrual phase for male clients
      if (field.id === 'menstrualPhase' && clientGender === 'M') {
        return false;
      }

      const hasAny = this.fieldHasAnyValue(field, bloodworkAnalysisHistory);
      // When hideBlanks is true: only fields with data (incl. menstrualPhase via altValue).
      // When hideBlanks is false: show all fields (incl. menstrualPhase) if not male.
      return hideBlanks ? hasAny : true; // default hide; show all when unchecked
    });

    // If nothing to show, don't render a table at all. A section can be made
    // ENTIRELY of text markers — a urinalysis panel is exactly that — so an
    // empty field list is no longer an empty section.
    if (visibleFields.length === 0 && textMarkers.length === 0) {
      return null;
    }

    // The Notes column: one cell for the whole section, spanning its rows. On
    // screen it is there for every section table, for the practitioner to
    // fill. In the PDF it prints on a with-notes export, on every table, note
    // or no note: a practitioner who prints the report writes in the empty
    // ones by hand. Practitioners who don't want that turn it off in Settings
    // (showComparisonNotesInPdf). The without-notes export never prints it.
    const { sectionKey, isPdf, blankNotes } = opts;
    const { shown: sectionNote } = sectionKey
      ? this.noteBlocksFor(sectionKey, bloodworkAnalysisHistory)
      : { shown: '' };
    // The note belongs to the section, not to a page of it: it prints beside
    // the first chunk and the continuations get the same column, empty. An
    // empty cell is what sectionNoteCell already draws for a section with no
    // note, so this needs nothing of its own.
    const shownNote = blankNotes ? '' : sectionNote;
    const showNotesColumn =
      !!sectionKey &&
      (isPdf
        ? !!this.state.withNotes && this.notesColumnInPdfEnabled()
        : this.notesColumnOnScreenEnabled());
    // The cell hangs off the first row and spans the rest.
    const noteRowSpan = visibleFields.length + textMarkers.length;
    const isEditingNotes =
      showNotesColumn &&
      !isPdf &&
      this.state.editingNotesSectionKey === sectionKey;
    // One section at a time, so a draft can never be lost to an Edit click
    // elsewhere on the page.
    const anotherSectionEditing =
      !!this.state.editingNotesSectionKey &&
      this.state.editingNotesSectionKey !== sectionKey;

    return (
      <Table
        striped
        bordered
        hover
        // With a Notes column, the other columns shrink to their content and
        // Notes takes the rest (App.css, .comparison-with-notes).
        className={
          showNotesColumn
            ? 'labsmartz-table comparison-with-notes'
            : 'labsmartz-table'
        }
        id={sectionKey && !isPdf ? comparisonTableDomId(sectionKey) : undefined}
      >
        <thead>
          <tr>
            <th>Marker</th>
            <th>Units</th>
            {bloodworkAnalysisHistory.map(
              (bloodworkAnalysis: BloodworkAnalysis, index: number) => {
                return (
                  <th key={index} className="comparison-num">
                    {stackedDate(bloodworkAnalysis.bloodwork.collectionDate)}
                  </th>
                );
              },
            )}
            <th>Trend</th>
            {/* The range columns read as a number line either side of
                Optimal, and each pair's header carries a band of that zone's
                color (the same values the cell tints use, shared as variables
                in index.css) so the header row doubles as their legend. A band
                under the heading, not a filled heading and not a filled
                column: these are static reference numbers and must not outweigh
                the client's results beside them. An optimal VALUE is still
                untinted in the body. */}
            {showAlarm && (
              <th className="comparison-range-alarm comparison-num">
                {stackedLabel('Alarm', 'Min')}
              </th>
            )}
            {showLab && (
              <th className="comparison-range-lab comparison-num">
                {stackedLabel('Lab', 'Min')}
              </th>
            )}
            <th className="comparison-range-optimal comparison-optimal-open comparison-num">
              {stackedLabel('Optimal', 'Min')}
            </th>
            <th className="comparison-range-optimal comparison-optimal-close comparison-num">
              {stackedLabel('Optimal', 'Max')}
            </th>
            {showLab && (
              <th className="comparison-range-lab comparison-num">
                {stackedLabel('Lab', 'Max')}
              </th>
            )}
            {showAlarm && (
              <th className="comparison-range-alarm comparison-num">
                {stackedLabel('Alarm', 'Max')}
              </th>
            )}
            {showNotesColumn && (
              <th className="comparison-notes-col" style={{ minWidth: '160px' }}>
                <span
                  className="d-flex align-items-center justify-content-between"
                  style={{ gap: '8px' }}
                >
                  <span>Notes</span>
                  {!isPdf &&
                    (isEditingNotes ? (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginRight: '4px' }}
                          disabled={this.state.savingNotes}
                          onClick={() => this.saveSectionNote(sectionKey!)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={this.state.savingNotes}
                          onClick={this.cancelEditingNotes}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={anotherSectionEditing}
                        title={
                          anotherSectionEditing
                            ? 'Save or cancel the notes you are editing first'
                            : undefined
                        }
                        onClick={() =>
                          this.startEditingNotes(sectionKey!, shownNote)
                        }
                      >
                        Edit
                      </button>
                    ))}
                </span>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {visibleFields.map((field: PanelField, rowIdx: number) => {
            const valueCells = bloodworkAnalysisHistory.map((bwa, colIdx) => {
              const pm: PanelMarker | undefined = this.findPanelMarker(
                field,
                bwa,
              );

              if (pm && field.id === 'menstrualPhase') {
                return (
                  <td key={`v-${rowIdx}-${colIdx}`}>
                    {pm.altValue === MenstrualPhase.NONE ? (
                      <>
                        {MenstrualPhase.NONE}
                        <br />
                        (children&apos;s ranges)
                      </>
                    ) : (
                      pm.altValue
                    )}
                  </td>
                );
              }

              return (
                <td
                  key={`v-${rowIdx}-${colIdx}`}
                  className={`comparison-num ${pm ? this.getClassName(pm) : ''}`}
                >
                  {pm?.hasValue?.() ? this.displayValue(pm) : null}
                  &nbsp;&nbsp;
                  {pm ? this.getIcon(pm) : null}
                </td>
              );
            });

            const lastPm = this.findPanelMarker(
              field,
              bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 1],
            );

            // The marker whose reference range fills the six range columns.
            //
            // For a shipped marker this is always the newest test: its range
            // comes from the global BloodworkRange catalog, so every analysis
            // resolves one whether or not a value was entered. A CUSTOM marker
            // only carries a range on the tests it was actually typed into, so
            // one that was entered on an earlier test and left off the newest
            // would show six blank columns. Fall back to the most recent test
            // that does have a range — that is also the most recent version of
            // the range if the practitioner revised it between tests.
            const rangePm = this.findRangeBearingPanelMarker(
              field,
              bloodworkAnalysisHistory,
              lastPm,
            );

            let trend: React.ReactNode = null;
            if (
              lastPm?.hasValue?.() &&
              lastPm.previousPanelMarker?.hasValue?.() &&
              !(lastPm.isOptimal() && lastPm.previousPanelMarker.isOptimal())
            ) {
              const improved =
                Math.abs(lastPm.percentFromMean) <
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              const worsened =
                Math.abs(lastPm.percentFromMean) >
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              trend = improved ? (
                <img src={thumbs_up} alt="" />
              ) : worsened ? (
                <img src={thumbs_down} alt="" />
              ) : null;
            }

            return (
              <tr key={`r-${rowIdx}`}>
                {/* The dot says the bands beside it are the practitioner's
                    own, not LabSmarts'. It matters more here than on the
                    analysis screen: a marker can appear to IMPROVE between two
                    dates purely because the range moved under both of them,
                    and the six bound columns to the right print the new
                    numbers for every column, including the older test. Screen
                    only — the comparison PDF is the client's too. */}
                <td>
                  {field.getComparisonLabel()}
                  {isPdf ? null : <MyRangeDot panelMarker={rangePm} />}
                </td>
                <td>{rangePm?.displayUnits?.(true) ?? field.getChartUnits()}</td>
                {valueCells}
                <td style={{ textAlign: 'center' }}>{trend}</td>
                {/*
                  displayBound() rather than the raw range number: it converts
                  to the display units AND rounds to the marker's precision.
                  These six columns previously printed the stored float
                  unrounded, so a derived bound could read "4.3999999".
                */}
                {showAlarm && (
                  <td className="comparison-range-num">
                    {rangePm?.displayBound?.(rangePm?.range?.alarmMin) ?? ''}
                  </td>
                )}
                {showLab && (
                  <td className="comparison-range-num">
                    {rangePm?.displayBound?.(rangePm?.range?.labMin) ?? ''}
                  </td>
                )}
                <td className="comparison-range-num comparison-optimal-open">
                  {rangePm?.displayBound?.(rangePm?.range?.optimalMin) ?? ''}
                </td>
                <td className="comparison-range-num comparison-optimal-close">
                  {rangePm?.displayBound?.(rangePm?.range?.optimalMax) ?? ''}
                </td>
                {showLab && (
                  <td className="comparison-range-num">
                    {rangePm?.displayBound?.(rangePm?.range?.labMax) ?? ''}
                  </td>
                )}
                {showAlarm && (
                  <td className="comparison-range-num">
                    {rangePm?.displayBound?.(rangePm?.range?.alarmMax) ?? ''}
                  </td>
                )}
                {showNotesColumn &&
                  rowIdx === 0 &&
                  this.sectionNoteCell(shownNote, isEditingNotes, noteRowSpan)}
              </tr>
            );
          })}
          {/* Under the charted rows, in first-appearance order. A result with
              no number cannot be sorted among ones that have them, and this is
              also the order a lab report prints: the measurements, then the
              observations. */}
          {textMarkers.map((textMarker, rowIdx) => {
            const expected = (textMarker.expected || '').trim();
            return (
              <tr key={`t-${rowIdx}`}>
                <td>{textMarker.label}</td>
                {/* No units: the words are the result. */}
                <td />
                {bloodworkAnalysisHistory.map((bwa, colIdx) => {
                  const text = this.textValueFor(textMarker, bwa);
                  const differs =
                    matchesExpectedText(text, expected) === false;
                  return (
                    <td
                      key={`tv-${rowIdx}-${colIdx}`}
                      className={
                        differs
                          ? 'comparison-text-result comparison-text-result-differs'
                          : 'comparison-text-result'
                      }
                    >
                      {text}
                    </td>
                  );
                })}
                {/* No trend: an arrow between two words would be claiming a
                    direction that does not exist. */}
                <td />
                {/* The range columns have nothing to hold for a text marker,
                    so they carry the one thing it can be judged against
                    instead. */}
                <td colSpan={rangeColumnCount} className="comparison-text-expected">
                  {expected ? `Expected: ${expected}` : ''}
                </td>
                {showNotesColumn &&
                  visibleFields.length === 0 &&
                  rowIdx === 0 &&
                  this.sectionNoteCell(shownNote, isEditingNotes, noteRowSpan)}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  practitionerNotesTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    isPdf: boolean = false,
  ) {
    // One row per collection date, carrying that test's Insights note AND its
    // Section Notes — a test whose notes were all typed in the section tabs has
    // no Insights note at all, and must still appear here.
    const notesArray = bloodworkAnalysisHistory
      .map(bwa => ({
        bloodwork: bwa.bloodwork,
        // Carried so the row can edit Section Notes, which is a consolidated
        // body rather than a stored field (see PractitionerNoteRow).
        bloodworkAnalysis: bwa,
        insights: insightsTextFor(bwa?.bloodwork),
        sectionNotes: sectionNotesTextFor(bwa),
      }))
      .filter(entry => entry.insights || entry.sectionNotes);

    if (notesArray.length === 0) {
      return null;
    }

    // Sort newer to older
    let sortedNotes = [...notesArray];
    sortedNotes.sort(
      (a, b) =>
        new Date(b.bloodwork.collectionDate).getTime() -
        new Date(a.bloodwork.collectionDate).getTime(),
    );

    // The Section Notes column only earns its width when the practitioner has
    // it switched on AND some test actually has section notes.
    const showSectionNotes =
      this.state.showSectionNotes && sortedNotes.some(e => !!e.sectionNotes);

    if (isPdf) {
      // Pre-paginated in JS: html2pdf can't split a table row, so each chunk is
      // one page's worth of rows and the heading repeats as "(cont.)".
      const noteChunks = paginateNoteRows(
        sortedNotes.map(entry => ({
          collectionDate: entry.bloodwork.collectionDate,
          insights: entry.insights,
          sectionNotes: entry.sectionNotes,
        })),
        { showSectionNotes },
      );

      return noteChunks.map((chunk, chunkIdx) => (
        // pageBreakInside: 'auto' overrides the .pdf-section avoid rule so the
        // wrapper isn't ALSO whole-pushed to a fresh page by html2pdf. Without it,
        // the wrapper push + the inner html2pdf__page-break stack into a double
        // break, leaving a blank page before each chunk. The inner .section-block
        // keeps its own page-break-inside: avoid, so the chunk table stays whole.
        // (Matches the Overall Progress & Trends chunk below.)
        <div
          key={`note-table-${chunkIdx}`}
          className="pdf-section"
          style={{ pageBreakInside: 'auto' }}
        >
          <div className="html2pdf__page-break" />
          <div style={{ height: TOP_OF_PAGE_SPACER }} />
          {/* Blue header + outline, matching the Disclaimer and Overall
              Progress & Trends cards. NOT "disclaimer-panel": that class is in
              html2pdf's break-inside:avoid list, which would stop these chunks
              from paginating. */}
          <div className="section-block">
            <Card style={{ marginTop: 0 }}>
              <Card.Header className="py-1">
                {showSectionNotes
                  ? 'Insights & Section Notes by Collection Date'
                  : 'Insights by Collection Date'}
                {chunkIdx > 0 ? ' (cont.)' : ''}
              </Card.Header>
              <Card.Body className="p-0">
                <Table
                  bordered
                  className="labsmartz-table labsmartz-notes-table"
                  style={{ marginTop: 0, marginBottom: 0, width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
                        Collection Date
                      </th>
                      <th style={showSectionNotes ? { width: '45%' } : undefined}>
                        Insights
                      </th>
                      {showSectionNotes && <th>Section Notes</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, idx) => {
                      const noteCell = (content: string, key: string) => (
                        <td>
                          <div
                            style={{
                              ...NOTE_WRAP,
                              color: NOTE_TEXT_COLOR,
                            }}
                          >
                            {content.split('\n').map((line: string, lIdx: number) => (
                              <div
                                key={`${key}-${lIdx}`}
                                style={{
                                  pageBreakInside: 'avoid',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {renderNoteLine(line)}
                              </div>
                            ))}
                          </div>
                        </td>
                      );
                      return (
                        // No zebra striping: these rows are text panels, not data
                        // rows, and a shaded one reads as a grey box beside the
                        // white Overall Progress & Trends card.
                        <tr key={`note-${chunkIdx}-${idx}`}>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {item.collectionDate}
                            </Moment>
                            {item.isCont && (
                              <span
                                style={{ fontSize: '0.8em', marginLeft: '4px' }}
                              >
                                (cont.)
                              </span>
                            )}
                          </td>
                          {noteCell(item.insightsSplit || '', 'ins')}
                          {showSectionNotes &&
                            noteCell(item.sectionSplit || '', 'sec')}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>
          <div style={{ height: '8px' }} />
        </div>
      ));
    }

    // For web UI, no chunking needed. Just show the sorted array.
    return (
      // Not striped — see labsmartz-notes-table in index.css.
      <Table bordered hover className="labsmartz-table labsmartz-notes-table">
        <thead>
          <tr>
            <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
              Collection Date
            </th>
            <th style={showSectionNotes ? { width: '40%' } : undefined}>
              Insights
            </th>
            {showSectionNotes && (
              <th style={{ width: '37%' }}>Section Notes</th>
            )}
            <th style={{ width: showSectionNotes ? '8%' : '10%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedNotes.map((entry, idx) => {
            return (
              <PractitionerNoteRow
                // Keyed by test, not position: the row list is filtered to the
                // tests that have notes, so a test gaining its first note
                // shifts every index below it — and an index key would hand one
                // row's typed-but-unsaved note to a different test.
                key={entry.bloodwork.id || `idx-${idx}`}
                bloodwork={entry.bloodwork}
                bloodworkAnalysis={entry.bloodworkAnalysis}
                bloodworkEntryStore={this.props.bloodworkEntryStore}
                sectionNotesText={entry.sectionNotes}
                showSectionNotesColumn={showSectionNotes}
              />
            );
          })}
        </tbody>
      </Table>
    );
  }

  onHidePdfModal() {
    this.props.onHidePdfModal();
    this.setState({
      showPdfModal: false,
      numResultsToCompare: NUM_RESULTS_TO_COMPARE,
      withNotes: false,
    });
  }

  render() {
    const { bloodworkRanges, currentUserProfile } = this.props;
    const {
      showUpgradeModal,
      showPdfModal,
      numResultsToCompare,
      headerLogoSrc,
      furtherConsiderations,
    } = this.state;

    let { bloodworks } = this.props;
    if (!bloodworks || bloodworks.length === 0) {
      return null;
    }

    if (!currentUserProfile.showClientComparisonTable) {
      return null;
    }

    bloodworks = bloodworks.slice(0, numResultsToCompare);

    const bloodworkAnalysisHistory = this.analysisHistory(
      bloodworks.reverse(),
      bloodworkRanges,
      currentUserProfile?.customMarkerSections,
    );
    const today = new Date().toISOString();
    const mostRecentBloodwork = bloodworks[bloodworks.length - 1];
    // Same record the Notes column reads and saves through.
    const actualClient = this.comparisonClient() || mostRecentBloodwork.client;

    // Fallback logo if preview URL hasn’t resolved yet
    const fallbackLogo =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    const logoSrc = headerLogoSrc || fallbackLogo;
    console.log(
      '[BloodworkComparison] Render - headerLogoSrc:',
      headerLogoSrc ? `${headerLogoSrc.substring(0, 50)}...` : 'null',
    );

    // Check if the whiteBloodCells marker has any value across the history
    const whiteBloodCellsField = PanelField.ALL_FIELDS.find(
      f => f.id === 'whiteBloodCells',
    );
    const whiteBloodCellsHasValues = whiteBloodCellsField
      ? this.fieldHasAnyValue(whiteBloodCellsField, bloodworkAnalysisHistory)
      : false;

    return (
      <>
        <PDFModal
          show={showPdfModal}
          title="Test Results Comparison Tables"
          client={actualClient}
          collectionDate={mostRecentBloodwork.collectionDate}
          elementId="pdf-comparison-results"
          onHide={() => this.onHidePdfModal()}
          preloadedLogoDataUrl={headerLogoSrc}
        >
          <div
            id="pdf-comparison-results"
            style={{ paddingBottom: 10, marginBottom: 10, transform: 'none' }}
          >
            <style>
              {`
                /* Page-break divs: force a break but take up no vertical space */
                #pdf-comparison-results .html2pdf__page-break {
                  height: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  border: 0 !important;
                  page-break-after: always !important;
                }

                #pdf-comparison-results h3,
                #pdf-comparison-results h5 {
                  margin-top: 8px !important;
                }

                #pdf-comparison-results table.labsmartz-table {
                  margin-top: 0 !important;
                }

                /* Keep each section's title glued to its table. This is the ONLY
                   "keep-together" primitive html2pdf 0.9.1 honors: break-after/
                   before:avoid are ignored by its pagebreak engine, but an element
                   with break-inside:avoid that is <= 1 page tall is pushed whole to
                   the next page when it would straddle a boundary (see
                   node_modules/html2pdf.js/src/plugin/pagebreaks.js, lines 104-113).
                   Sections that can exceed one page are forced onto a fresh page by
                   the renderer, so their title still lands at the top and is never
                   orphaned. */
                #pdf-comparison-results .pdf-section,
                #pdf-comparison-results .pdf-section .section-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* Keep each data row intact. A tall section's table is allowed
                   to flow across a page boundary (see the isTall branch in
                   renderPdfSectionsAnalysisOrder); without this, html2pdf would
                   slice through a row mid-height at the break. */
                #pdf-comparison-results table.labsmartz-table tr {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* The Disclaimer is pushed whole to a fresh page by the avoid
                   rule, which lands it EXACTLY on the page boundary — and the
                   slice grid rounds down to whole device pixels, so the top of
                   the blue header was being shaved onto the page above. The
                   padding is inside the pushed element, so those lost pixels
                   are blank. Same trick as .pdf-keep-together-block in
                   containers/pdf.css. */
                #pdf-comparison-results .pdf-disclaimer-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                  padding-top: 24px;
                }
              `}
            </style>
            <br />
            <br />
            <br />
            <div className="text-center">
              <Image
                id="logo"
                src={logoSrc}
                alt="Current Logo"
                fluid
                style={{
                  maxHeight: '100px',
                  maxWidth: '400px',
                  objectFit: 'contain',
                }}
                onError={(e: any) => {
                  const img = e.target as HTMLImageElement;
                  if (img.src !== fallbackLogo) {
                    img.src = fallbackLogo;
                  }
                }}
              />
            </div>
            <div>
              <br />
              <br />
              {/* Defaults to "Lab Comparison Report", not the old "Blood Work
                  Comparison Report", matching the analysis PDF's cover: a
                  comparison can line up an organic acids, stool or hair test
                  the practitioner added, so the specimen cannot be in the
                  title. Report Settings can replace it outright. */}
              <h3 className="text-center">
                <strong>
                  {resolveComparisonReportTitle(currentUserProfile)}
                </strong>
              </h3>
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            {/* html2pdf__page-break will add a page break after the html element */}
            <div>
              <h5>
                <strong>Prepared for</strong>
              </h5>
              <h5>
                {actualClient.firstName} {actualClient.lastName}
              </h5>
              <br />
              {currentUserProfile.preparedBy && (
                <>
                  <h5>
                    <strong>Prepared by</strong>
                  </h5>
                  <h5>{currentUserProfile.preparedBy}</h5>
                  <br />
                </>
              )}
              <h5>
                <strong>Most Recent Collection Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">
                  {mostRecentBloodwork.collectionDate}
                </Moment>
              </h5>
              <br />
              <h5>
                <strong>Analysis Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">{today}</Moment>
              </h5>
              <br />
            </div>

            <div
              className="html2pdf__page-break"
              style={{ height: 0, margin: 0, padding: 0, border: 0 }}
            />

            {this.renderPdfSectionsAnalysisOrder(
              bloodworkAnalysisHistory,
              actualClient.gender,
              whiteBloodCellsHasValues,
            )}

            {/* Practitioner Notes in PDF */}
            {(() => {
              // Section notes count as notes here too — a test with section
              // notes and no Insights note still belongs in this block.
              const anyHasNotes = bloodworkAnalysisHistory.some(bwa =>
                comparisonNotesTextFor(bwa),
              );
              if (!this.printsInsightsNotesBlock() || !anyHasNotes)
                return null;
              const notesTable = this.practitionerNotesTable(
                bloodworkAnalysisHistory,
                true,
              );
              return notesTable;
            })()}

            {/* Overall Practitioner Notes in PDF */}
            {(() => {
              if (!this.printsOverallProgressBlock()) return null;
              const noteText = actualClient?.overallPractitionerNotes || '';
              if (!noteText.trim()) return null;

              // Use the same pagination mechanism (see paginateNoteRows for
              // where the page geometry comes from: ~744 CSS px per page, and
              // why the safety margin sits here rather than in WRAP_CHARS).
              const MAX_PAGE_HEIGHT = 700;
              // Top spacer (TOP_OF_PAGE_SPACER) + card header + offsets.
              const HEADER_HEIGHT = 76;
              const LINE_HEIGHT = 18;

              const lines = noteText.split('\n');
              const overallChunks: string[][] = [];
              let currentLines: string[] = [];
              let currentChunkHeight = HEADER_HEIGHT;

              lines.forEach(line => {
                // This box is the full width of a LANDSCAPE page: ~1063px of
                // text over a ~5.6px average character width, so ~189
                // characters on one rendered line. The REAL figure, no cushion
                // — counting a 160-char bullet as two lines (the old /120)
                // inflated the estimate by ~2x and broke the panel a page
                // early. The margin is in MAX_PAGE_HEIGHT instead.
                const WRAP_CHARS = 189;
                const wrapCount = Math.max(1, Math.ceil(line.length / WRAP_CHARS));
                const lineH = wrapCount * LINE_HEIGHT;

                if (
                  currentChunkHeight + lineH > MAX_PAGE_HEIGHT &&
                  currentLines.length > 0
                ) {
                  overallChunks.push(currentLines);
                  currentLines = [line];
                  currentChunkHeight = HEADER_HEIGHT + lineH;
                } else {
                  currentLines.push(line);
                  currentChunkHeight += lineH;
                }
              });
              if (currentLines.length > 0) overallChunks.push(currentLines);

              return overallChunks.map((linesChunk, idx) => (
                <div
                  key={`overall-chunk-${idx}`}
                  className="pdf-section"
                  style={{ pageBreakInside: 'auto' }}
                >
                  <div className="html2pdf__page-break" />
                  <div style={{ height: TOP_OF_PAGE_SPACER }} />
                  {/* Blue header + outline, matching the Disclaimer and the
                      Insights card. A plain Card, not "disclaimer-panel" \u2014
                      that class is in html2pdf's break-inside:avoid list and
                      would stop these chunks paginating. */}
                  <div className="section-block">
                    <Card style={{ marginTop: 0 }}>
                      <Card.Header className="py-1">
                        Overall Progress &amp; Trends{idx > 0 ? ' (cont.)' : ''}
                      </Card.Header>
                      <Card.Body
                        style={{
                          padding: '10px',
                          ...NOTE_WRAP,
                          minHeight: '2em',
                          color: NOTE_TEXT_COLOR,
                        }}
                      >
                        {linesChunk.map((line, lIdx, lArr) => (
                          <div
                            key={lIdx}
                            style={{
                              pageBreakInside: 'avoid',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {/* Formatted, like the on-screen field \u2014 the
                                practitioner's **bold** must not print as markup.
                                Still one div per line so the chunker's page math
                                is unchanged. */}
                            {renderNoteLine(line)}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </div>
                  <div style={{ height: '12px' }} />
                </div>
              ));
            })()}
            {/* Disclaimer */}
            {/* No explicit html2pdf__page-break here: .disclaimer-panel is in
                html2pdf's `avoid` list (see PDFModal's pagebreak opts), so the
                panel is already pushed whole to a fresh page if it would straddle
                a boundary. A forced break ON TOP of that risks html2pdf's
                phantom-blank-page bug — when the preceding content ends exactly
                at a page edge, the forced "after" break expands its fill-pad to a
                full blank page, leaving the disclaimer page looking empty. Letting
                the keep-together rule place it avoids that entirely.

                The wrapper's padding-top is what stops the blue header being
                shaved onto the previous page. html2pdf's "avoid" pad lands the
                element on the page boundary EXACTLY (pagebreaks.js:97-100),
                and the canvas is then sliced on a grid rounded down to whole
                device pixels (worker.js:181) — so a boundary-hugging top edge
                loses a pixel or two to the page above. Padding INSIDE the
                pushed element gives those pixels something blank to eat.
                Mirrors .pdf-keep-together-block in containers/pdf.css. */}
            <div className="pdf-disclaimer-block">
              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </div>
          </div>
        </PDFModal>

        {showPdfModal ? null : (
          <>
            {/* The DOM id stays `blood-work-comparison`: it is the scroll anchor
                bloodwork-analysis-container restores to, not something a
                practitioner reads. The heading says "Test Results Comparison
                Tables" so it matches the rest of the practitioner chrome (the
                Test Results menu, "Test Results Analysis"). The PDF cover page keeps "Lab
                Comparison Report" by default: that document is the client's, and
                "Lab" covers an organic acids or hair panel without naming the
                specimen. */}
            <Card id="blood-work-comparison" ref={this.comparisonCardRef as any}>
              <Card.Header>
                Test Results Comparison Tables&nbsp;
                {/* Smaller than the title: the controls have outgrown the
                    header's width at the title's size. */}
                <span
                  className="float-right d-flex align-items-center"
                  style={{ gap: '14px', fontSize: '12px' }}
                >
                  {/* Export Raw Data is LIVE in all envs. The AI "Generate with
                      AI" summary below stays localhost-only (see gates further down). */}
                  {(
                    <>
                      {/* iconSize: this row carries seven controls, so it runs at 12px
                          and scales EVERY icon to 15px inline, its own
                          Export-to-PDF icons included. Without the override this
                          file icon takes the house 20px and dwarfs them. */}
                      <RawDataExportControl
                        label="Export All Client Data"
                        tooltip="Download all of this client's data — every test, plus notes and analyses — as a file to use with an AI"
                        scopeLine="This file holds every test this client has, with their notes and analyses."
                        tabsLocation="Overall Progress &amp; Trends"
                        onExport={this.exportJson}
                        idSuffix="all-client"
                        iconSize="15px"
                      />
                      {this.headerDivider()}
                    </>
                  )}
                  {/* lsm-styled-checkbox (App.css) draws the white box AND
                      the checkmark, same as the Section Notes toggle below.
                      No scroll-to-title on these two: dropping columns leaves
                      the rows where they are, so the practitioner stays put. */}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '8px' }}
                  >
                    <label
                      htmlFor="hideAlarmToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Hide Alarm
                    </label>
                    <OverlayTrigger
                      overlay={this.hideAlarmTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '15px' }}
                      />
                    </OverlayTrigger>
                    <input
                      id="hideAlarmToggle"
                      className="lsm-styled-checkbox lsm-styled-checkbox--compact"
                      type="checkbox"
                      checked={this.alarmColumnsHidden()}
                      onChange={e =>
                        this.setState({ hideAlarmColumns: e.target.checked })
                      }
                    />
                  </span>
                  {this.headerDivider()}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '8px' }}
                  >
                    <label
                      htmlFor="hideLabToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Hide Lab
                    </label>
                    <OverlayTrigger
                      overlay={this.hideLabTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '15px' }}
                      />
                    </OverlayTrigger>
                    <input
                      id="hideLabToggle"
                      className="lsm-styled-checkbox lsm-styled-checkbox--compact"
                      type="checkbox"
                      checked={this.labColumnsHidden()}
                      onChange={e =>
                        this.setState({ hideLabColumns: e.target.checked })
                      }
                    />
                  </span>
                  {this.headerDivider()}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '8px' }}
                  >
                    <label
                      htmlFor="hideEmptyRowsToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Hide Blank Rows
                    </label>
                    <OverlayTrigger
                      overlay={this.hideBlankRowsTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '15px' }}
                      />
                    </OverlayTrigger>
                    {/* lsm-styled-checkbox (App.css) draws the white box AND
                        the checkmark; --compact is the header's smaller box. */}
                    <input
                      id="hideEmptyRowsToggle"
                      className="lsm-styled-checkbox lsm-styled-checkbox--compact"
                      type="checkbox"
                      checked={!this.state.showHiddenRows}
                      onChange={e =>
                        this.setState(
                          { showHiddenRows: !e.target.checked },
                          this.scrollComparisonTitleIntoView,
                        )
                      }
                    />
                  </span>
                  {this.headerDivider()}
                  {/* Each export icon carries its own label and is one click
                      target with it — the same treatment as the Test Results
                      Analysis header. The two download glyphs are near
                      identical, so a single shared "Export to PDF" label left
                      the with/without-notes choice to a hover tooltip. */}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <OverlayTrigger
                      overlay={this.exportInfoTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        onClick={this.openExportInfoWindow}
                        style={{ cursor: 'pointer', fontSize: '15px' }}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportTooltip()}
                      placement="bottom"
                    >
                      <span
                        onClick={() => this.export(false)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: 'white',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export to PDF w/o notes
                        <FontAwesomeIcon
                          icon={faFileDownload}
                          style={{ fontSize: '15px' }}
                        />
                      </span>
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportWithNotesTooltip()}
                      placement="bottom"
                    >
                      <span
                        onClick={() => this.export(true)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          color: 'white',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export to PDF w/ notes
                        <FontAwesomeIcon
                          icon={faFileDownloadSolid}
                          style={{ fontSize: '15px' }}
                        />
                      </span>
                    </OverlayTrigger>
                  </span>
                </span>
              </Card.Header>
              <Card.Body>
                {this.renderPdfNoteIncludes()}
                {/* Sections render in the test-results analysis order
                    (see comparisonSectionOrder). */}
                {this.buildComparisonSections(
                  bloodworkAnalysisHistory,
                  actualClient.gender,
                  whiteBloodCellsHasValues,
                ).map(section => (
                  <React.Fragment key={section.key}>
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>{section.title}</strong>
                    </h5>
                    {section.table}
                    <div style={{ height: '8px' }} />
                  </React.Fragment>
                ))}

                {/* Insights note OR section notes — either one earns the block. */}
                {(() => {
                  const anyNotes = bloodworkAnalysisHistory.some(bwa =>
                    comparisonNotesTextFor(bwa),
                  );
                  if (!anyNotes) return null;
                  // The toggle only appears when there are section notes to
                  // show, and the title follows what the table is actually
                  // rendering (same rule as practitionerNotesTable).
                  const anySectionNotes = bloodworkAnalysisHistory.some(bwa =>
                    sectionNotesTextFor(bwa),
                  );
                  const showingSectionNotes =
                    this.state.showSectionNotes && anySectionNotes;
                  return (
                    // Blue header + outline, so this reads as the same kind of
                    // panel as Overall Progress & Trends and the Disclaimer.
                    <Card className="mt-4">
                      <Card.Header className="d-flex align-items-center justify-content-between">
                        <span>
                          {showingSectionNotes
                            ? 'Insights & Section Notes by Collection Date'
                            : 'Insights by Collection Date'}
                        </span>
                        {anySectionNotes && (
                          <span
                            className="d-flex align-items-center"
                            style={{ gap: '10px' }}
                          >
                            <label
                              htmlFor="showSectionNotesToggle"
                              style={{
                                marginBottom: 0,
                                fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              Show Section Notes
                            </label>
                            <OverlayTrigger
                              overlay={this.showSectionNotesTooltip()}
                              placement="left"
                            >
                              <FontAwesomeIcon
                                icon="info-circle"
                                style={{ cursor: 'help', fontSize: '16px' }}
                              />
                            </OverlayTrigger>
                            {/* lsm-styled-checkbox carries the white-on-blue box
                                AND the :checked checkmark (App.css) — a plain
                                inline-styled box would render blank when on. */}
                            <input
                              id="showSectionNotesToggle"
                              className="lsm-styled-checkbox"
                              type="checkbox"
                              checked={this.state.showSectionNotes}
                              onChange={e =>
                                this.handleShowSectionNotesChange(
                                  e.target.checked,
                                )
                              }
                            />
                          </span>
                        )}
                      </Card.Header>
                      <Card.Body className="p-0">
                        {this.practitionerNotesTable(
                          bloodworkAnalysisHistory,
                          false,
                        )}
                      </Card.Body>
                    </Card>
                  );
                })()}

                {/* id anchors Save's scroll-back to the top of this card. */}
                <Card className="mt-4" id="overall-progress">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <span>Overall Progress & Trends</span>
                    <span
                      className="d-flex align-items-center"
                      style={{ gap: '14px' }}
                    >
                      {aiProgressSummaryVisible() && (
                        <Button
                          size="sm"
                          variant="light"
                          onClick={this.onGenerateSummary}
                          disabled={this.state.generatingSummary}
                        >
                          {this.state.generatingSummary
                            ? 'Generating…'
                            : 'Generate with AI'}
                        </Button>
                      )}
                      {/* The same control as the report toolbar above, not a
                          variant: identical label, file and popover, so the
                          two read as one button placed where it is wanted
                          rather than two that might differ.

                          It is here because this card is 300-odd lines of
                          markup below that toolbar — every comparison table
                          the client has — and this is where its output gets
                          pasted. Scrolling back to the top to start the loop
                          is friction at exactly the moment the practitioner
                          knows what they want. Nothing moved out of the
                          toolbar, so no muscle memory breaks. */}
                      <RawDataExportControl
                        label="Export All Client Data"
                        tooltip="Download all of this client's data — every test, plus notes and analyses — as a file to use with an AI"
                        scopeLine="This file holds every test this client has, with their notes and analyses."
                        tabsLocation="Overall Progress &amp; Trends"
                        onExport={this.exportJson}
                        idSuffix="overall-progress"
                      />
                    </span>
                  </Card.Header>
                  <Card.Body>
                    {aiProgressSummaryVisible() && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#a0522d',
                          fontWeight: 600,
                          marginBottom: '6px',
                        }}
                      >
                        ⚠️ AI-generated draft. This text appears on the client's
                        report — please review and edit it before saving and
                        printing.
                      </div>
                    )}
                    {/* Two tabs, matching the analysis report's Insights
                        card and the two parts the AI export answers in.
                        Client Summary is the long-standing
                        overallPractitionerNotes, unchanged and still the half
                        that prints at the end of this report; the other is a
                        new client-level field nothing prints.

                        The labels carry the relabel this needed: the stored
                        field is called "Overall Practitioner Notes", which
                        sounds private and never was, and putting a genuinely
                        private sibling beside it without saying which is
                        which would make that name actively dangerous. */}
                    <Tabs
                      id="overall-progress-tabs"
                      mountOnEnter
                      activeKey={this.state.overallProgressTab}
                      onSelect={(k: any) =>
                        this.setState({ overallProgressTab: k || 'progress-client' })
                      }
                    >
                      <Tab eventKey="progress-client" title="Client Summary">
                        <br />
                        <ClientNotes
                          title="Notes for the client. Printed in the PDF. Click Save when finished."
                          client={actualClient}
                          clientEntryStore={this.props.clientEntryStore}
                          aiDraft={this.state.aiSummaryDraft}
                          scrollAnchorId="overall-progress"
                        />
                      </Tab>
                      <Tab eventKey="progress-practitioner" title="Practitioner Notes">
                        <br />
                        {/* No aiDraft: the "Generate with AI" button drafts
                            CLIENT-FACING text (AI_PROGRESS_SUMMARY_PROMPT), so
                            it belongs to the tab above and must not overwrite
                            whatever is being kept here. */}
                        <ClientNotes
                          title="Notes for you. Never printed in the PDF. Click Save when finished."
                          client={actualClient}
                          clientEntryStore={this.props.clientEntryStore}
                          field="overallPractitionerPrivateNotes"
                          scrollAnchorId="overall-progress"
                        />
                      </Tab>
                    </Tabs>
                  </Card.Body>
                </Card>

              {/* Disclaimer in UI */}
              <div className="mt-4">
                <DisclaimerSection furtherConsiderations={furtherConsiderations} />
              </div>

              </Card.Body>
            </Card>



            <GenericModal
              show={showUpgradeModal}
              title="Export to PDF Feature Not Available"
              text="Export to PDF feature is not available with a free account."
              link="https://labsmarts.com/pricing"
              linkText="Click here to upgrade."
              onHide={() => this.setState({ showUpgradeModal: false })}
            />
          </>
        )}
      </>
    );
  }
}
